/**
 * MultiUrlComparisonTable.tsx
 *
 * 다중 URL 분석 결과를 비교하는 테이블 컴포넌트
 * - 옵션 A: 수평 테이블 (사이트를 열로 배치)
 * - 옵션 B: 수직 테이블 (사이트를 행으로 배치)
 * - 현재 선택된 탭의 데이터를 하이라이트
 * - 레이아웃 토글 기능 제공
 */

import React, { useState, useEffect } from 'react';
import { getRatingGrade, getRatingColor } from '../../../utils/overallRating';
import { PageSpeedResult, ServiceData } from '../../../types';

// ============================================================================
// 온라인 커머스(브랜드 쇼핑몰) 현실적 기준
// - Google 이상적 기준: LCP 2.5초, FCP 1.8초, TBT 300ms
// - 커머스 현실: 상품 이미지, 리뷰, 추천 등 필수 콘텐츠로 인해 더 느림
// ============================================================================
const ECOMMERCE_THRESHOLD = {
  // LCP (Largest Contentful Paint) - 메인 콘텐츠 로딩
  LCP_GOOD: 4.0,       // 커머스에서 4초 이하면 우수
  LCP_WARNING: 8.0,    // 8초 이상이면 개선 권장
  LCP_CRITICAL: 12.0,  // 12초 이상이면 심각

  // FCP (First Contentful Paint) - 첫 화면 표시
  FCP_GOOD: 3.0,       // 커머스에서 3초 이하면 우수
  FCP_WARNING: 5.0,    // 5초 이상이면 개선 권장
  FCP_CRITICAL: 8.0,   // 8초 이상이면 심각

  // TBT (Total Blocking Time) - 인터랙션 지연
  TBT_GOOD: 600,       // 커머스에서 600ms 이하면 우수
  TBT_WARNING: 1500,   // 1.5초 이상이면 개선 권장
  TBT_CRITICAL: 3000,  // 3초 이상이면 심각

  // 페이지 크기 (MB) - 상품 이미지 포함
  SIZE_GOOD: 20,       // 20MB 이하면 우수
  SIZE_WARNING: 40,    // 40MB 이상이면 개선 권장
  SIZE_CRITICAL: 60    // 60MB 이상이면 심각
};

// V2 designSystem
const designSystem = {
  colors: {
    primary: '#2467E7',
    primaryDark: '#1B4DB8',
    primaryLight: '#E8F2FF',
    secondary: '#6C5CE7',
    accent: '#00D4FF',
    success: '#00B894',
    warning: '#FDCB6E',
    danger: '#E17055',
    textPrimary: '#2D3748',
    textSecondary: '#718096',
    textMuted: '#A0AEC0',
    bgPrimary: '#F7FAFC',
    bgSecondary: '#EDF2F7',
    border: '#E2E8F0'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)',
    '2xl': '0 25px 50px rgba(0, 0, 0, 0.15)'
  },
  typography: {
    h2: {
      fontSize: '1.875rem',
      fontWeight: '600',
      lineHeight: '1.3'
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: '600',
      lineHeight: '1.3'
    },
    body: {
      fontSize: '1rem',
      fontWeight: '400',
      lineHeight: '1.5'
    },
    caption: {
      fontSize: '0.875rem',
      fontWeight: '500',
      lineHeight: '1.4'
    }
  }
};

// 테이블 스타일 정의 (Step 3)
const tableStyles = {
  // 컨테이너 스타일
  container: {
    background: 'white',
    borderRadius: '1.5rem',
    padding: '2rem',
    boxShadow: designSystem.shadows.lg,
    border: `1px solid ${designSystem.colors.border}`,
    marginBottom: '3rem'
  },

  // 헤더 스타일
  header: {
    ...designSystem.typography.h3,
    color: designSystem.colors.primary,
    marginBottom: '1.5rem',
    textAlign: 'center' as const,
    margin: '0 0 1.5rem 0'
  },

  // 테이블 기본 스타일
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.9rem'
  },

  // 테이블 헤더 셀 스타일
  th: {
    ...designSystem.typography.caption,
    color: 'white',
    padding: '0.75rem 1rem',
    borderBottom: `2px solid ${designSystem.colors.primary}`,
    textAlign: 'center' as const,  // 가운데 정렬
    fontWeight: '600',
    backgroundColor: designSystem.colors.primary  // 원래 진한 블루
  },

  // 테이블 데이터 셀 스타일
  td: {
    ...designSystem.typography.body,
    padding: '1rem',
    borderBottom: `1px solid ${designSystem.colors.border}`,
    color: designSystem.colors.textPrimary,
    transition: 'background-color 0.2s ease'
  },

  // 현재 선택된 컬럼 하이라이트 (옵션 A - 수평 테이블용)
  activeColumn: {
    backgroundColor: `${designSystem.colors.primary}30`,
    fontWeight: '600'
  },

  // 현재 선택된 컬럼의 헤더 스타일 (배경 변경 없이 표시)
  activeColumnHeader: {
    fontWeight: '700',
    borderBottom: `4px solid ${designSystem.colors.accent}`,
    position: 'relative' as const,
    boxShadow: `inset 0 -3px 0 0 ${designSystem.colors.accent}`
  },

  // 현재 선택된 행 하이라이트 (옵션 B - 수직 테이블용)
  activeRow: {
    backgroundColor: `${designSystem.colors.primary}25`,
    fontWeight: '600',
    borderLeft: `5px solid ${designSystem.colors.primary}`
  },

  // 클릭 가능한 셀 스타일
  clickable: {
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: designSystem.colors.primaryLight,
      transform: 'translateY(-1px)'
    }
  },

  // 스크롤 래퍼
  scrollWrapper: {
    overflowX: 'auto' as const,
    overflowY: 'visible' as const,
    marginBottom: '1rem',
    // 커스텀 스크롤바 스타일
    '::-webkit-scrollbar': {
      height: '8px'
    },
    '::-webkit-scrollbar-track': {
      backgroundColor: designSystem.colors.bgSecondary,
      borderRadius: '4px'
    },
    '::-webkit-scrollbar-thumb': {
      backgroundColor: designSystem.colors.border,
      borderRadius: '4px',
      ':hover': {
        backgroundColor: designSystem.colors.textMuted
      }
    }
  },

  // 안내 메시지 스타일
  hint: {
    marginTop: '1rem',
    fontSize: '0.875rem',
    color: designSystem.colors.textMuted,
    textAlign: 'center' as const,
    fontStyle: 'italic' as const
  },

  // 점수 배지 스타일
  scoreBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '0.5rem',
    fontWeight: '600',
    fontSize: '0.9rem',
    textAlign: 'center' as const
  },

  // 메트릭 라벨 셀 스타일 (옵션 A의 첫 번째 컬럼)
  metricLabel: {
    fontWeight: '600',
    color: 'white',
    backgroundColor: designSystem.colors.primaryDark,  // 진한 블루
    width: '200px',  // 항목명 열 너비
    minWidth: '200px',
    maxWidth: '200px'
  }
};

// AnalysisResult 타입 정의 (AppV2와 완전히 동일)
interface AnalysisResult {
  url: string;
  batchId?: string;
  urlIndex?: number;
  failed?: boolean;
  error?: string;
  pageSpeedData?: PageSpeedResult;
  llmAnalysis?: string;
  timestamp?: string;
  services?: ServiceData[];
  importantInfo?: any;
  resourceSummary?: any;
  customRatings?: {
    mobile: number;
    desktop: number;
    overall: number;
  };
}

// LLM 총평 데이터 인터페이스 - 구조화된 형식 (Case 1용)
interface StructuredLLMSummary {
  commonStrengths: string[];      // 공통 장점
  commonWeaknesses: string[];     // 주요 개선사항
  siteInsights: {                 // 페이지별 특징
    siteName: string;
    url: string;
    highlights: string[];         // 주요 강점
    issues: string[];             // 개선 필요 사항
  }[];
}



// Props 인터페이스
interface MultiUrlComparisonTableProps {
  /** 다중 분석 결과 배열 */
  batchResults: AnalysisResult[];
  /** 현재 선택된 URL 인덱스 */
  selectedUrlIndex?: number;
  /** URL 라벨 맵 (저장된 분석 또는 세션) */
  urlLabels?: { [key: string]: string } | null;
  /** URL 클릭 시 콜백 함수 */
  onUrlClick: (url: string, index: number) => void;
  /** 테스트 케이스 (1, 2, 3, 4, 5) */
  testCase?: 1 | 2 | 3 | 4 | 5;
}

// 샘플 LLM 총평 생성 함수 - 구조화된 형식 (Case 1용)
const generateStructuredLLMSummary = (
  batchResults: AnalysisResult[],
  getUrlLabel: (url: string) => string
): StructuredLLMSummary => {
  const validResults = batchResults.filter(r => !r.failed);

  // 공통 장점 분석
  const commonStrengths: string[] = [];
  const avgScore = validResults.reduce((sum, r) => sum + (r.customRatings?.overall || 0), 0) / validResults.length;
  const avgFCP = validResults.reduce((sum, r) => sum + (r.pageSpeedData?.mobile?.firstContentfulPaint || 0), 0) / validResults.length;
  const avgLCP = validResults.reduce((sum, r) => sum + (r.pageSpeedData?.mobile?.largestContentfulPaint || 0), 0) / validResults.length;

  // 커머스 현실 기준 적용
  if (avgScore >= 70) {
    commonStrengths.push('커머스 사이트 기준 양호한 성능 점수를 보이고 있습니다');
  }
  if (avgFCP < ECOMMERCE_THRESHOLD.FCP_GOOD) {
    commonStrengths.push(`첫 콘텐츠 표시(FCP)가 빠른 편입니다 (평균 ${avgFCP.toFixed(1)}초)`);
  }
  if (avgLCP < ECOMMERCE_THRESHOLD.LCP_GOOD) {
    commonStrengths.push(`최대 콘텐츠 표시(LCP)가 커머스 기준 우수합니다 (평균 ${avgLCP.toFixed(1)}초)`);
  }

  // 주요 개선사항 분석 (커머스 현실 기준)
  const commonWeaknesses: string[] = [];
  const avgTBT = validResults.reduce((sum, r) => sum + (r.pageSpeedData?.mobile?.totalBlockingTime || 0), 0) / validResults.length;
  const avgSize = validResults.reduce((sum, r) => sum + (r.pageSpeedData?.mobile?.pageSize?.totalSize || 0), 0) / validResults.length / 1024 / 1024;

  if (avgScore < 50) {
    commonWeaknesses.push('전반적인 성능 최적화가 필요합니다');
  }
  if (avgTBT > ECOMMERCE_THRESHOLD.TBT_WARNING) {
    commonWeaknesses.push(`JavaScript 실행 시간이 길어 사용자 상호작용이 지연될 수 있습니다 (평균 ${(avgTBT/1000).toFixed(1)}초)`);
  }
  if (avgSize > ECOMMERCE_THRESHOLD.SIZE_WARNING) {
    commonWeaknesses.push(`페이지 크기가 커서 로딩 시간에 영향을 줄 수 있습니다 (평균 ${avgSize.toFixed(1)}MB)`);
  }

  // 페이지별 특징 분석 (커머스 현실 기준)
  const siteInsights = validResults.map(result => {
    const siteName = getUrlLabel(result.url);
    const score = result.customRatings?.overall || 0;
    const fcp = result.pageSpeedData?.mobile?.firstContentfulPaint || 0;
    const lcp = result.pageSpeedData?.mobile?.largestContentfulPaint || 0;
    const tbt = result.pageSpeedData?.mobile?.totalBlockingTime || 0;
    const size = (result.pageSpeedData?.mobile?.pageSize?.totalSize || 0) / 1024 / 1024;

    const highlights: string[] = [];
    const issues: string[] = [];

    // 강점 분석 (커머스 기준)
    if (score >= 80) {
      highlights.push(`우수한 전체 성능 점수 (${Math.round(score)}점)`);
    } else if (score >= 65) {
      highlights.push(`양호한 성능 점수 (${Math.round(score)}점)`);
    }

    if (fcp < ECOMMERCE_THRESHOLD.FCP_GOOD) {
      highlights.push(`빠른 첫 콘텐츠 표시 (${fcp.toFixed(1)}초)`);
    }

    if (lcp < ECOMMERCE_THRESHOLD.LCP_GOOD) {
      highlights.push(`우수한 LCP 성능 (${lcp.toFixed(1)}초)`);
    }

    if (tbt < ECOMMERCE_THRESHOLD.TBT_GOOD) {
      highlights.push(`낮은 총 차단 시간 (${Math.round(tbt)}ms)`);
    }

    if (size < ECOMMERCE_THRESHOLD.SIZE_GOOD) {
      highlights.push(`적정한 페이지 크기 (${size.toFixed(1)}MB)`);
    }

    // 개선사항 분석 (커머스 기준)
    if (score < 50) {
      issues.push(`낮은 성능 점수로 전반적인 최적화 필요 (${Math.round(score)}점)`);
    }

    if (fcp > ECOMMERCE_THRESHOLD.FCP_WARNING) {
      issues.push(`느린 첫 콘텐츠 표시 (${fcp.toFixed(1)}초) - 커머스 권장: ${ECOMMERCE_THRESHOLD.FCP_GOOD}초 이하`);
    }

    if (lcp > ECOMMERCE_THRESHOLD.LCP_WARNING) {
      issues.push(`느린 LCP (${lcp.toFixed(1)}초) - 커머스 권장: ${ECOMMERCE_THRESHOLD.LCP_GOOD}초 이하`);
    }

    if (tbt > ECOMMERCE_THRESHOLD.TBT_WARNING) {
      issues.push(`높은 총 차단 시간 (${(tbt/1000).toFixed(1)}초) - JavaScript 최적화 권장`);
    }

    if (size > 50) {
      issues.push(`과도한 페이지 크기 (${size.toFixed(1)}MB) - 리소스 최적화 필요`);
    }

    return {
      siteName,
      url: result.url,
      highlights,
      issues
    };
  });

  return {
    commonStrengths,
    commonWeaknesses,
    siteInsights
  };
};




/**
 * MultiUrlComparisonTable 컴포넌트
 */
const MultiUrlComparisonTable: React.FC<MultiUrlComparisonTableProps> = ({
  batchResults,
  selectedUrlIndex,
  urlLabels,
  onUrlClick,
  testCase = 2  // 기본값: Case 2 (메인 프로덕션 버전)
}) => {
  // 실패한 사이트 필터링 (원본 인덱스 유지)
  const validResults = batchResults
    .map((result, originalIndex) => ({ ...result, originalIndex }))
    .filter(result => !result.failed);

  // [E] 테이블용 디바이스 선택 상태 (최상위 레벨에서 선언)
  const [selectedDevicePerUrl, setSelectedDevicePerUrl] = useState<{ [url: string]: 'mobile' | 'desktop' }>(() => {
    // 초기값: 모든 URL을 mobile로 설정
    const initial: { [url: string]: 'mobile' | 'desktop' } = {};
    validResults.forEach(result => {
      initial[result.url] = 'mobile';
    });
    return initial;
  });

  // [Z] 섹션용 전역 디바이스 선택 상태
  const [zSectionDevice, setZSectionDevice] = useState<'mobile' | 'desktop'>('desktop');

  // 비즈니스 인사이트 상태 (LLM 기반 동적 생성) - 디바이스별 캐싱
  const [businessInsightCache, setBusinessInsightCache] = useState<{
    desktop: any | null;
    mobile: any | null;
  }>({ desktop: null, mobile: null });
  const [isLoadingInsight, setIsLoadingInsight] = useState(false);

  // 현재 디바이스에 해당하는 캐싱된 인사이트
  const businessInsight = businessInsightCache[zSectionDevice];

  // F 섹션 AI 개선 버전 캐싱 (URL:디바이스 조합으로 캐싱)
  const [improvedInsightsCache, setImprovedInsightsCache] = useState<{ [key: string]: any }>({});
  const [isLoadingPageInsights, setIsLoadingPageInsights] = useState<{ [url: string]: boolean }>({});

  // 현재 디바이스에 맞는 캐시 키 생성 헬퍼
  const getPageInsightCacheKey = (url: string, device: 'mobile' | 'desktop') => `${url}:${device}`;

  // 현재 디바이스에 맞는 개선 버전 인사이트 가져오기
  const getImprovedInsight = (url: string) => improvedInsightsCache[getPageInsightCacheKey(url, zSectionDevice)];

  // 비즈니스 인사이트 API 호출 (디바이스별 캐싱 지원)
  useEffect(() => {
    // Case 2가 아니거나, 최소 3개 사이트가 없으면 실행하지 않음
    if (testCase !== 2 || validResults.length < 3) {
      return;
    }

    // 이미 캐싱된 결과가 있으면 API 호출 스킵
    if (businessInsightCache[zSectionDevice] !== null) {
      console.log(`📦 캐싱된 ${zSectionDevice} 인사이트 사용`);
      return;
    }

    const fetchBusinessInsight = async () => {
      setIsLoadingInsight(true);
      console.log(`🔄 ${zSectionDevice} 인사이트 API 호출 시작...`);

      try {
        const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3300';
        const response = await fetch(`${BACKEND_URL}/api/generate-business-insight`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            batchResults,
            device: zSectionDevice, // 디바이스 정보 전달
            // 커머스 현실 기준 전달 (LLM 프롬프트에 적용)
            ecommerceThreshold: ECOMMERCE_THRESHOLD
          })
        });

        if (!response.ok) {
          throw new Error('비즈니스 인사이트 생성 실패');
        }

        const data = await response.json();
        if (data.success) {
          // 현재 디바이스에 캐싱
          setBusinessInsightCache(prev => ({
            ...prev,
            [zSectionDevice]: data.insight
          }));
          console.log(`✅ ${zSectionDevice} 인사이트 캐싱 완료`);
        }
      } catch (error) {
        console.error('비즈니스 인사이트 로딩 오류:', error);
        // 에러 시에도 null로 캐싱하여 무한 재시도 방지
        setBusinessInsightCache(prev => ({
          ...prev,
          [zSectionDevice]: null
        }));
      } finally {
        setIsLoadingInsight(false);
      }
    };

    fetchBusinessInsight();
  }, [testCase, batchResults, validResults.length, zSectionDevice, businessInsightCache]);

  // F 섹션: 모든 URL의 개선 버전 인사이트 자동 로드 (개선 버전이 메인이므로)
  useEffect(() => {
    validResults.forEach(result => {
      const hasCache = getImprovedInsight(result.url);
      const isLoading = isLoadingPageInsights[result.url];

      // 캐시가 없고 로딩 중이 아니면 자동 로드
      if (!hasCache && !isLoading) {
        console.log(`🔄 개선 버전 인사이트 자동 로드: ${result.url} (${zSectionDevice})`);
        fetchImprovedInsights(result.url, result, zSectionDevice);
      }
    });
  }, [zSectionDevice, validResults]); // zSectionDevice 또는 validResults 변경 시 실행

  // 현재 선택된 URL 찾기
  const selectedUrl = selectedUrlIndex !== undefined ? batchResults[selectedUrlIndex]?.url : undefined;

  // URL 라벨 가져오기 (AppV2와 동일한 로직)
  const getUrlLabel = (url: string): string => {
    try {
      // 1. props로 전달된 urlLabels에서 먼저 조회 (저장된 분석의 경우)
      if (urlLabels && urlLabels[url]) {
        return urlLabels[url];
      }

      // 2. sessionStorage에서 조회 (현재 분석의 경우)
      const sessionLabels = sessionStorage.getItem('urlLabels');
      if (sessionLabels) {
        const labels = JSON.parse(sessionLabels);
        if (labels[url]) {
          return labels[url];
        }
      }

      // 3. 기본값: hostname 추출
      return new URL(url).hostname;
    } catch (error) {
      // URL 파싱 실패 시 원본 반환
      return url;
    }
  };

  // 개선 버전 인사이트 가져오기 함수 (디바이스별 캐싱 지원)
  const fetchImprovedInsights = async (url: string, result: any, device: 'mobile' | 'desktop') => {
    const cacheKey = getPageInsightCacheKey(url, device);

    // 이미 캐싱된 결과가 있으면 스킵
    if (improvedInsightsCache[cacheKey]) {
      console.log(`📦 캐싱된 페이지 인사이트 사용: ${url} (${device})`);
      return;
    }

    setIsLoadingPageInsights(prev => ({ ...prev, [url]: true }));
    try {
      // 선택된 디바이스에 맞는 PageSpeed 데이터 추출
      const pageSpeedData = device === 'mobile'
        ? result.pageSpeedData?.mobile
        : result.pageSpeedData?.desktop;

      console.log(`🔄 페이지 인사이트 API 호출: ${url} (${device})`);
      const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3300';
      const response = await fetch(`${BACKEND_URL}/api/generate-page-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pageSpeedData,
          url,
          device, // 디바이스 정보 전달
          // 커머스 현실 기준 전달 (LLM 프롬프트에 적용)
          ecommerceThreshold: ECOMMERCE_THRESHOLD
        })
      });

      if (!response.ok) {
        throw new Error('페이지 인사이트 생성 실패');
      }

      const data = await response.json();
      if (data.success) {
        // 디바이스별 캐시에 저장
        setImprovedInsightsCache(prev => ({ ...prev, [cacheKey]: data }));
        console.log(`✅ 페이지 인사이트 캐싱 완료: ${url} (${device})`);
      }
    } catch (error) {
      console.error('페이지 인사이트 로딩 오류:', error);
    } finally {
      setIsLoadingPageInsights(prev => ({ ...prev, [url]: false }));
    }
  };

  // 시간 포맷팅 (초 → "1.2s")
  const formatTime = (seconds?: number): string => {
    if (seconds === undefined || seconds === null) return '—';
    return `${Math.round(seconds * 10) / 10}s`;
  };

  // 용량 포맷팅 (bytes → "2.3MB")
  const formatSize = (bytes?: number): string => {
    if (bytes === undefined || bytes === null) return '—';
    const mb = bytes / 1024 / 1024;
    return `${Math.round(mb * 10) / 10}MB`;
  };

  // TBT 포맷팅 (밀리초 입력 → "120ms" 또는 "1.2s")
  const formatTBT = (milliseconds?: number): string => {
    if (milliseconds === undefined || milliseconds === null) return '—';

    // 1초(1000ms) 이상이면 초 단위로 표시
    if (milliseconds >= 1000) {
      return `${Math.round(milliseconds / 100) / 10}s`;
    }
    // 1초 미만이면 밀리초로 표시
    return `${Math.round(milliseconds)}ms`;
  };

  // 메트릭 색상 평가 (커머스 현실 기준)
  const getMetricColor = (metric: 'FCP' | 'LCP' | 'TBT', value?: number): string => {
    if (value === undefined || value === null) return designSystem.colors.textMuted;

    switch (metric) {
      case 'FCP':
        // 커머스 기준: < 3초 좋음, 3-5초 보통, > 5초 나쁨
        if (value < ECOMMERCE_THRESHOLD.FCP_GOOD) return designSystem.colors.success;
        if (value < ECOMMERCE_THRESHOLD.FCP_WARNING) return designSystem.colors.warning;
        return designSystem.colors.danger;

      case 'LCP':
        // 커머스 기준: < 4초 좋음, 4-8초 보통, > 8초 나쁨
        if (value < ECOMMERCE_THRESHOLD.LCP_GOOD) return designSystem.colors.success;
        if (value < ECOMMERCE_THRESHOLD.LCP_WARNING) return designSystem.colors.warning;
        return designSystem.colors.danger;

      case 'TBT':
        // 커머스 기준: < 600ms 좋음, 600-1500ms 보통, > 1500ms 나쁨
        if (value < ECOMMERCE_THRESHOLD.TBT_GOOD) return designSystem.colors.success;
        if (value < ECOMMERCE_THRESHOLD.TBT_WARNING) return designSystem.colors.warning;
        return designSystem.colors.danger;

      default:
        return designSystem.colors.textMuted;
    }
  };

  // 메트릭 상태 아이콘 (커머스 현실 기준)
  const getMetricIcon = (metric: 'FCP' | 'LCP' | 'TBT', value?: number): string => {
    if (value === undefined || value === null) return '—';

    switch (metric) {
      case 'FCP':
        if (value < ECOMMERCE_THRESHOLD.FCP_GOOD) return '✓';
        if (value < ECOMMERCE_THRESHOLD.FCP_WARNING) return '!';
        return '✗';

      case 'LCP':
        if (value < ECOMMERCE_THRESHOLD.LCP_GOOD) return '✓';
        if (value < ECOMMERCE_THRESHOLD.LCP_WARNING) return '!';
        return '✗';

      case 'TBT':
        if (value < ECOMMERCE_THRESHOLD.TBT_GOOD) return '✓';
        if (value < ECOMMERCE_THRESHOLD.TBT_WARNING) return '!';
        return '✗';

      default:
        return '—';
    }
  };

  // 페이지 크기 색상 평가 (커머스 현실 기준)
  const getPageSizeColor = (bytes?: number): string => {
    if (bytes === undefined || bytes === null) return designSystem.colors.textMuted;
    const mb = bytes / 1024 / 1024;

    // 커머스 기준: < 20MB 좋음, 20-40MB 보통, > 40MB 나쁨
    if (mb < ECOMMERCE_THRESHOLD.SIZE_GOOD) return designSystem.colors.success;
    if (mb < ECOMMERCE_THRESHOLD.SIZE_WARNING) return designSystem.colors.warning;
    return designSystem.colors.danger;
  };

  // 페이지 크기 아이콘 (커머스 현실 기준)
  const getPageSizeIcon = (bytes?: number): string => {
    if (bytes === undefined || bytes === null) return '—';
    const mb = bytes / 1024 / 1024;

    if (mb < ECOMMERCE_THRESHOLD.SIZE_GOOD) return '✓';
    if (mb < ECOMMERCE_THRESHOLD.SIZE_WARNING) return '!';
    return '✗';
  };

  // 페이지 크기 라벨 (커머스 현실 기준)
  const getPageSizeLabel = (bytes?: number): string => {
    if (bytes === undefined || bytes === null) return '';
    const mb = bytes / 1024 / 1024;

    if (mb < ECOMMERCE_THRESHOLD.SIZE_GOOD) return '좋음';
    if (mb < ECOMMERCE_THRESHOLD.SIZE_WARNING) return '보통';
    return '나쁨';
  };

  // LLM 총평 생성 (Case 1용 구조화된 형식)
  const structuredSummary = generateStructuredLLMSummary(batchResults, getUrlLabel);

  // ============================================================================
  // Case 2: Case 1 (VerticalTable) + HTML 문서 섹션 추가
  // ============================================================================
  if (testCase === 2) {
    // 디바이스 토글 핸들러 (상태는 최상위에서 선언됨)
    const toggleDevice = (url: string, device: 'mobile' | 'desktop') => {
      setSelectedDevicePerUrl(prev => ({ ...prev, [url]: device }));
    };

    // 점수 기반 상태 판단 헬퍼 함수
    const getStatusInfo = (score: number): { label: string; color: string; bgColor: string; emoji: string } => {
      if (score >= 85) {
        return { label: '우수', color: '#155724', bgColor: '#d4edda', emoji: '⭐' };
      } else if (score >= 70) {
        return { label: '양호', color: '#0c5460', bgColor: '#d1ecf1', emoji: '✅' };
      } else if (score >= 50) {
        return { label: '보통', color: '#856404', bgColor: '#fff3cd', emoji: '⚠️' };
      } else {
        return { label: '개선필요', color: '#721c24', bgColor: '#f8d7da', emoji: '🚨' };
      }
    };

    // 메트릭 색상 및 배지 스타일 헬퍼 (커머스 현실 기준)
    const getMetricStyle = (value: number, metric: 'fcp' | 'lcp' | 'tbt'): { bgColor: string; textColor: string } => {
      if (metric === 'fcp') {
        // 커머스 기준: < 3초 좋음, 3-5초 보통, > 5초 나쁨
        if (value < ECOMMERCE_THRESHOLD.FCP_GOOD) return { bgColor: '#d4edda', textColor: '#155724' };
        if (value < ECOMMERCE_THRESHOLD.FCP_WARNING) return { bgColor: '#fff3cd', textColor: '#856404' };
        return { bgColor: '#f8d7da', textColor: '#721c24' };
      } else if (metric === 'lcp') {
        // 커머스 기준: < 4초 좋음, 4-8초 보통, > 8초 나쁨
        if (value < ECOMMERCE_THRESHOLD.LCP_GOOD) return { bgColor: '#d4edda', textColor: '#155724' };
        if (value < ECOMMERCE_THRESHOLD.LCP_WARNING) return { bgColor: '#fff3cd', textColor: '#856404' };
        return { bgColor: '#f8d7da', textColor: '#721c24' };
      } else { // tbt (밀리초)
        // 커머스 기준: < 600ms 좋음, 600-1500ms 보통, > 1500ms 나쁨
        if (value < ECOMMERCE_THRESHOLD.TBT_GOOD) return { bgColor: '#d4edda', textColor: '#155724' };
        if (value < ECOMMERCE_THRESHOLD.TBT_WARNING) return { bgColor: '#fff3cd', textColor: '#856404' };
        return { bgColor: '#f8d7da', textColor: '#721c24' };
      }
    };

    // 점수 원형 배지 색상
    const getScoreBadgeGradient = (score: number): string => {
      if (score >= 90) return 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)';
      if (score >= 80) return 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)';
      if (score >= 70) return 'linear-gradient(135deg, #f7b733 0%, #fc4a1a 100%)';
      if (score >= 50) return 'linear-gradient(135deg, #f39c12 0%, #e74c3c 100%)';
      return 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)';
    };

    // [F] 섹션용: 주요 문제점 생성 헬퍼 (커머스 현실 기준)
    const generateIssues = (pageSpeedData: any): string[] => {
      const issues: string[] = [];
      const fcp = pageSpeedData?.firstContentfulPaint || 0;
      const lcp = pageSpeedData?.largestContentfulPaint || 0;
      const tbt = pageSpeedData?.totalBlockingTime || 0;
      const size = (pageSpeedData?.pageSize?.totalSize || 0) / 1024 / 1024;

      // 커머스 기준으로 심각한 경우만 이슈로 표시
      if (lcp > ECOMMERCE_THRESHOLD.LCP_WARNING) issues.push(`LCP ${lcp.toFixed(1)}초로 메인 콘텐츠 로딩이 느립니다`);
      if (fcp > ECOMMERCE_THRESHOLD.FCP_WARNING) issues.push(`FCP ${fcp.toFixed(1)}초로 첫 화면 표시가 지연됩니다`);
      if (tbt > ECOMMERCE_THRESHOLD.TBT_WARNING) issues.push(`TBT ${(tbt/1000).toFixed(1)}초로 인터랙션이 지연될 수 있습니다`);
      if (size > ECOMMERCE_THRESHOLD.SIZE_WARNING) issues.push(`페이지 크기 ${size.toFixed(1)}MB로 로딩에 영향을 줄 수 있습니다`);

      if (issues.length === 0) issues.push('커머스 사이트 기준으로 양호한 성능입니다');
      return issues;
    };

    // [F] 섹션용: 개선 권장사항 생성 헬퍼 (커머스 현실 기준)
    const generateRecommendations = (pageSpeedData: any): string[] => {
      const recommendations: string[] = [];
      const lcp = pageSpeedData?.largestContentfulPaint || 0;
      const fcp = pageSpeedData?.firstContentfulPaint || 0;
      const tbt = pageSpeedData?.totalBlockingTime || 0;
      const size = (pageSpeedData?.pageSize?.totalSize || 0) / 1024 / 1024;

      // 커머스 기준으로 실현 가능한 개선안 제시
      if (size > ECOMMERCE_THRESHOLD.SIZE_WARNING) recommendations.push('상품 이미지 WebP 포맷 전환 및 적정 해상도 조정');
      if (tbt > ECOMMERCE_THRESHOLD.TBT_WARNING) recommendations.push('불필요한 서드파티 스크립트 제거 또는 지연 로딩');
      if (lcp > ECOMMERCE_THRESHOLD.LCP_WARNING) recommendations.push('메인 배너 이미지 최적화 및 지연 로딩 적용');
      if (fcp > ECOMMERCE_THRESHOLD.FCP_WARNING) recommendations.push('서버 응답 시간 점검 및 렌더링 차단 리소스 확인');
      if (size > ECOMMERCE_THRESHOLD.SIZE_CRITICAL) recommendations.push('페이지당 표시 상품 수 조정 또는 무한 스크롤 적용');

      if (recommendations.length === 0) recommendations.push('현재 상태를 유지하며 정기적인 모니터링을 권장합니다');
      return recommendations;
    };

    // [B-1, B-2, C, Z] 섹션용: 종합 분석 데이터 계산 헬퍼
    const calculateOverallStats = (device: 'mobile' | 'desktop' = 'mobile') => {
      // 평균 점수 계산 (선택된 디바이스 기준)
      const scores = validResults.map(r => r.customRatings?.[device] || r.customRatings?.overall || 0);
      const avgScore = scores.reduce((sum, s) => sum + s, 0) / scores.length;

      // 최고/최저 점수 페이지 찾기
      let bestResult = validResults[0];
      let worstResult = validResults[0];
      validResults.forEach(result => {
        const score = result.customRatings?.[device] || result.customRatings?.overall || 0;
        const bestScore = bestResult.customRatings?.[device] || bestResult.customRatings?.overall || 0;
        const worstScore = worstResult.customRatings?.[device] || worstResult.customRatings?.overall || 0;
        if (score > bestScore) bestResult = result;
        if (score < worstScore) worstResult = result;
      });

      // 70점 이상 페이지 개수
      const goodPagesCount = scores.filter(s => s >= 70).length;

      // 최저 점수 페이지의 주요 이슈 (선택된 디바이스)
      const worstPageData = worstResult.pageSpeedData?.[device];
      const worstLcp = worstPageData?.largestContentfulPaint || 0;
      const worstTbt = worstPageData?.totalBlockingTime || 0;
      const worstFcp = worstPageData?.firstContentfulPaint || 0;

      // [C] 섹션용: 모든 메트릭 평균 계산 (선택된 디바이스)
      const fcpValues = validResults.map(r => {
        const data = r.pageSpeedData?.[device];
        return data?.firstContentfulPaint || 0;
      }).filter(v => v > 0);
      const avgFcp = fcpValues.length > 0 ? fcpValues.reduce((sum, v) => sum + v, 0) / fcpValues.length : 0;

      const lcpValues = validResults.map(r => {
        const data = r.pageSpeedData?.[device];
        return data?.largestContentfulPaint || 0;
      }).filter(v => v > 0);
      const avgLcp = lcpValues.length > 0 ? lcpValues.reduce((sum, v) => sum + v, 0) / lcpValues.length : 0;

      const tbtValues = validResults.map(r => {
        const data = r.pageSpeedData?.[device];
        return data?.totalBlockingTime || 0;
      }).filter(v => v > 0);
      const avgTbt = tbtValues.length > 0 ? tbtValues.reduce((sum, v) => sum + v, 0) / tbtValues.length : 0;

      const sizeValues = validResults.map(r => {
        const data = r.pageSpeedData?.[device];
        return data?.pageSize?.totalSize || 0;
      }).filter(v => v > 0);
      const avgSize = sizeValues.length > 0 ? (sizeValues.reduce((sum, v) => sum + v, 0) / sizeValues.length) / (1024 * 1024) : 0;

      // 상위 3개 페이지 (점수 기준, 선택된 디바이스)
      const topPages = [...validResults]
        .map(r => ({
          result: r,
          score: r.customRatings?.[device] || r.customRatings?.overall || 0
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 3);

      return {
        avgScore,
        bestResult,
        worstResult,
        goodPagesCount,
        totalPages: validResults.length,
        worstLcp,
        worstTbt,
        worstFcp,
        avgFcp,
        avgLcp,
        avgTbt,
        avgSize,
        topPages
      };
    };

    // 개발용 인덱스 라벨 스타일
    const indexLabelStyle: React.CSSProperties = {
      backgroundColor: '#f0f0f0',
      padding: '4px 12px',
      borderRadius: '4px',
      display: 'inline-block',
      fontSize: '13px',
      fontWeight: 'bold',
      marginBottom: '12px',
      color: '#666',
      fontFamily: 'monospace',
      border: '1px solid #ddd'
    };

    return (
      <div>
        {/*
          수평 테이블 (Case 2) - 현재 사용하지 않음 (2025.11.25 주석처리)
          나중에 필요시 주석 해제하여 사용 가능

        <VerticalTable
          validResults={validResults}
          selectedUrl={selectedUrl}
          getUrlLabel={getUrlLabel}
          formatTime={formatTime}
          formatSize={formatSize}
          formatTBT={formatTBT}
          getMetricColor={getMetricColor}
          getMetricIcon={getMetricIcon}
          getPageSizeColor={getPageSizeColor}
          getPageSizeIcon={getPageSizeIcon}
          getPageSizeLabel={getPageSizeLabel}
          onUrlClick={onUrlClick}
        />
        */}

        {/* HTML 문서 섹션들 (performance_dashboard_final.html) - AppV2 풀와이드 레이아웃 */}
        <div style={{
          margin: '2rem 0 0',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '2rem 5%',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontSize: '28px',
            marginBottom: '8px'
          }}>
            🚀 웹사이트 성능 종합 대시보드
          </h1>
        </div>

        {/* ========== 종합 분석 그리드 (B, Z-4, B-3, B-4) ========== */}
        <div style={{
          background: '#ffffff',
          borderTop: '4px solid #2467E7',
          padding: '2rem 5%'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <span style={{ fontSize: '32px' }}>💡</span>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>종합 분석 및 전문가 의견</h2>
          </div>

          <div style={{ marginBottom: '32px' }}>
          {/* 디바이스 토글 버튼 - 1번 카드 위 */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              display: 'inline-flex',
              background: '#e9ecef',
              borderRadius: '20px',
              padding: '2px',
              gap: '2px'
            }}>
              <button
                onClick={() => setZSectionDevice('desktop')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '18px',
                  border: 'none',
                  background: zSectionDevice === 'desktop' ? 'white' : 'transparent',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: zSectionDevice === 'desktop' ? '#667eea' : '#6c757d',
                  boxShadow: zSectionDevice === 'desktop' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s ease'
                }}>💻 PC</button>
              <button
                onClick={() => setZSectionDevice('mobile')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '18px',
                  border: 'none',
                  background: zSectionDevice === 'mobile' ? 'white' : 'transparent',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: zSectionDevice === 'mobile' ? '#667eea' : '#6c757d',
                  boxShadow: zSectionDevice === 'mobile' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s ease'
                }}>📱 Mobile</button>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '20px',
            marginBottom: '40px'
          }}>
            {(() => {
              const stats = calculateOverallStats(zSectionDevice);
              const bestScore = stats.bestResult.customRatings?.[zSectionDevice] || stats.bestResult.customRatings?.overall || 0;
              const worstScore = stats.worstResult.customRatings?.[zSectionDevice] || stats.worstResult.customRatings?.overall || 0;
              const targetScore = Math.min(85, Math.round(stats.avgScore) + 10);

              // 통일된 카드 스타일
              const cardStyle = {
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease',
                border: '1px solid #e9ecef',
                display: 'flex' as const,
                flexDirection: 'column' as const,
                minHeight: '440px'
              };

              const cardHeaderStyle = {
                marginBottom: '20px',
                minHeight: '48px',
                display: 'flex' as const,
                flexDirection: 'column' as const,
                justifyContent: 'flex-start' as const
              };

              const cardTitleStyle = {
                fontSize: '16px',
                color: '#212529',
                fontWeight: '700',
                marginBottom: '6px',
                lineHeight: '1.3',
                margin: 0
              };

              const cardSubtitleStyle = {
                fontSize: '13px',
                color: '#868e96',
                fontWeight: '400',
                lineHeight: '1.4'
              };

              const scoreSectionStyle = {
                marginBottom: '20px',
                paddingBottom: '20px',
                borderBottom: '1px solid #e9ecef',
                minHeight: '100px',
                display: 'flex' as const,
                flexDirection: 'column' as const,
                justifyContent: 'flex-start' as const
              };

              const scoreStyle = (color: string) => ({
                fontSize: '56px',
                fontWeight: '700',
                lineHeight: '1',
                letterSpacing: '-1px',
                color
              });

              const scoreLabelStyle = {
                fontSize: '13px',
                color: '#868e96',
                fontWeight: '400',
                marginTop: '8px'
              };

              const cardContentStyle = {
                flex: 1,
                display: 'flex' as const,
                flexDirection: 'column' as const,
                gap: '20px'
              };

              const contentSectionStyle = {
                display: 'flex' as const,
                flexDirection: 'column' as const
              };

              const sectionLabelStyle = {
                fontSize: '12px',
                color: '#868e96',
                fontWeight: '600',
                marginBottom: '12px'
              };

              const dataBoxStyle = {
                background: '#f8f9fa',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#495057',
                lineHeight: '1.6'
              };

              const listItemStyle = {
                display: 'flex' as const,
                alignItems: 'flex-start' as const,
                gap: '8px',
                fontSize: '13px',
                color: '#495057',
                marginBottom: '8px',
                lineHeight: '1.5'
              };

              const itemIconStyle = (color: string) => ({
                flexShrink: 0,
                fontWeight: '700',
                marginTop: '1px',
                color
              });

              return (
                <>
                  {/* Card 1: 사이트 성능 요약 */}
                  <div style={cardStyle}>
                    {/* 헤더 */}
                    <div style={cardHeaderStyle}>
                      <div style={cardTitleStyle}>사이트 성능 요약</div>
                      <div style={cardSubtitleStyle}>평균 성능 점수</div>
                    </div>

                    {/* 점수 섹션 */}
                    <div style={scoreSectionStyle}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={scoreStyle('#4c6ef5')}>{Math.round(stats.avgScore)}점</div>
                        <div style={scoreLabelStyle}>Web Vitals (평균)</div>
                      </div>
                    </div>

                    {/* 콘텐츠 */}
                    <div style={cardContentStyle}>
                      {/* 섹션 1: 주요 지표 */}
                      <div style={contentSectionStyle}>
                        <div style={sectionLabelStyle}>주요 지표</div>
                        <div style={dataBoxStyle}>
                          FCP {stats.avgFcp.toFixed(1)}초 | LCP {stats.avgLcp.toFixed(1)}초<br />
                          TBT {Math.round(stats.avgTbt)}ms
                        </div>
                      </div>

                      {/* 섹션 2: 사이트 상태 */}
                      <div style={contentSectionStyle}>
                        <div style={sectionLabelStyle}>사이트 상태</div>
                        <div style={listItemStyle}>
                          <span style={itemIconStyle('#51cf66')}>✓</span>
                          <span>최고 페이지는 {Math.round(bestScore)}점</span>
                        </div>
                        <div style={listItemStyle}>
                          <span style={itemIconStyle('#51cf66')}>✓</span>
                          <span>최저 페이지는 {Math.round(worstScore)}점</span>
                        </div>
                        <div style={{...listItemStyle, marginBottom: 0}}>
                          <span style={itemIconStyle('#51cf66')}>✓</span>
                          <span>{stats.goodPagesCount}개 페이지가 70점 이상</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: 문제 페이지 분석 */}
                  <div style={cardStyle}>
                    {/* 헤더 */}
                    <div style={cardHeaderStyle}>
                      <div style={cardTitleStyle}>문제 페이지 분석</div>
                      <div style={cardSubtitleStyle}>최저 점수 페이지 • {getUrlLabel(stats.worstResult.url)}</div>
                    </div>

                    {/* 점수 섹션 */}
                    <div style={scoreSectionStyle}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={scoreStyle('#ff6b6b')}>{Math.round(worstScore)}점</div>
                        <div style={scoreLabelStyle}>개선 필요</div>
                      </div>
                    </div>

                    {/* 콘텐츠 */}
                    <div style={cardContentStyle}>
                      {/* 섹션 1: 주요 문제 지표 */}
                      <div style={contentSectionStyle}>
                        <div style={sectionLabelStyle}>주요 문제 지표</div>
                        {stats.worstLcp > ECOMMERCE_THRESHOLD.LCP_GOOD && (
                          <div style={listItemStyle}>
                            <span style={itemIconStyle('#ff6b6b')}>•</span>
                            <span>LCP <span style={{ color: '#ff6b6b', fontWeight: '600' }}>{stats.worstLcp.toFixed(1)}초</span></span>
                          </div>
                        )}
                        {stats.worstFcp > ECOMMERCE_THRESHOLD.FCP_GOOD && (
                          <div style={listItemStyle}>
                            <span style={itemIconStyle('#ff6b6b')}>•</span>
                            <span>FCP <span style={{ color: '#ff6b6b', fontWeight: '600' }}>{stats.worstFcp.toFixed(1)}초</span></span>
                          </div>
                        )}
                      </div>

                      {/* 섹션 2: 개선 가이드 */}
                      <div style={contentSectionStyle}>
                        <div style={sectionLabelStyle}>개선 가이드</div>
                        {stats.worstLcp > ECOMMERCE_THRESHOLD.LCP_GOOD && (
                          <div style={listItemStyle}>
                            <span style={itemIconStyle('#ff6b6b')}>•</span>
                            <span>Hero 이미지 최적화 필요</span>
                          </div>
                        )}
                        {stats.worstTbt > ECOMMERCE_THRESHOLD.TBT_GOOD && (
                          <div style={{...listItemStyle, marginBottom: 0}}>
                            <span style={itemIconStyle('#ff6b6b')}>•</span>
                            <span>불필요 스크립트 제거</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card 3: 우수 페이지 분석 */}
                  <div style={cardStyle}>
                    {/* 헤더 */}
                    <div style={cardHeaderStyle}>
                      <div style={cardTitleStyle}>우수 페이지 분석</div>
                      <div style={cardSubtitleStyle}>최고 점수 페이지 • {getUrlLabel(stats.bestResult.url)}</div>
                    </div>

                    {/* 점수 섹션 */}
                    <div style={scoreSectionStyle}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={scoreStyle('#51cf66')}>{Math.round(bestScore)}점</div>
                        <div style={scoreLabelStyle}>우수</div>
                      </div>
                    </div>

                    {/* 콘텐츠 */}
                    <div style={cardContentStyle}>
                      {/* 섹션 1: TOP 3 페이지 */}
                      <div style={contentSectionStyle}>
                        <div style={sectionLabelStyle}>TOP 3 페이지</div>
                        {stats.topPages.slice(0, 3).map((page, idx) => (
                          <div key={idx} style={{
                            ...dataBoxStyle,
                            marginBottom: idx < 2 ? '8px' : '0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <span style={{ fontSize: '13px', color: '#495057', fontWeight: '500' }}>
                              {idx + 1}) {getUrlLabel(page.result.url)}
                            </span>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#51cf66' }}>
                              {Math.round(page.score)}점
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* 섹션 2: 강점 분석 */}
                      <div style={contentSectionStyle}>
                        <div style={sectionLabelStyle}>강점 분석</div>
                        <div style={listItemStyle}>
                          <span style={itemIconStyle('#51cf66')}>•</span>
                          <span>{getUrlLabel(stats.topPages[0]?.result.url)} 로딩 우선순위</span>
                        </div>
                        <div style={{...listItemStyle, marginBottom: 0}}>
                          <span style={itemIconStyle('#51cf66')}>•</span>
                          <span>전반적으로 양호한 성능</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 4: 개선 기대 효과 (ROI) */}
                  <div style={cardStyle}>
                    {/* 헤더 */}
                    <div style={cardHeaderStyle}>
                      <div style={cardTitleStyle}>개선 기대 효과 (ROI)</div>
                      <div style={cardSubtitleStyle}>성능 점수 향상 시</div>
                    </div>

                    {/* 점수 섹션 - ROI는 특별한 형태 */}
                    <div style={scoreSectionStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                        <span style={{
                          fontSize: '36px',
                          fontWeight: '700',
                          lineHeight: '1.4',
                          color: '#868e96'
                        }}>
                          {Math.round(stats.avgScore)}
                        </span>
                        <span style={{
                          fontSize: '24px',
                          color: '#adb5bd'
                        }}>
                          →
                        </span>
                        <span style={{
                          fontSize: '36px',
                          fontWeight: '700',
                          lineHeight: '1.4',
                          color: '#4c6ef5'
                        }}>
                          {targetScore}+
                        </span>
                      </div>
                      <div style={scoreLabelStyle}>예상 효과</div>
                    </div>

                    {/* 콘텐츠 */}
                    <div style={cardContentStyle}>
                      {/* 섹션 1: 전환율 증가 */}
                      <div style={contentSectionStyle}>
                        <div style={sectionLabelStyle}>전환율 증가</div>
                        <div style={{
                          ...dataBoxStyle,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{ fontSize: '13px', color: '#495057', fontWeight: '500' }}>전환율</span>
                          <span style={{ fontSize: '15px', fontWeight: '700', color: '#51cf66' }}>+10~15%</span>
                        </div>
                      </div>

                      {/* 섹션 2: 이탈률 감소 */}
                      <div style={contentSectionStyle}>
                        <div style={sectionLabelStyle}>이탈률 감소</div>
                        <div style={{
                          ...dataBoxStyle,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{ fontSize: '13px', color: '#495057', fontWeight: '500' }}>이탈률</span>
                          <span style={{ fontSize: '15px', fontWeight: '700', color: '#ff6b6b' }}>-15~20%</span>
                        </div>
                      </div>

                      {/* 섹션 3: SEO 개선 */}
                      <div style={contentSectionStyle}>
                        <div style={sectionLabelStyle}>SEO 개선</div>
                        <div style={{
                          ...dataBoxStyle,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{ fontSize: '13px', color: '#495057', fontWeight: '500' }}>검색 노출</span>
                          <span style={{ fontSize: '15px', fontWeight: '700', color: '#4c6ef5' }}>향상</span>
                        </div>
                      </div>
                    </div>

                    {/* 푸터 */}
                    <div style={{
                      marginTop: 'auto',
                      paddingTop: '16px'
                    }}>
                      <div style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        lineHeight: '1.5',
                        background: '#f8f9fa',
                        padding: '10px',
                        borderRadius: '6px',
                        textAlign: 'center'
                      }}>
                        빠른 성능 개선 우선 대응 권장
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          </div>

          <div style={{ marginTop: '32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>

              {/* [B-3-1] 비즈니스 임팩트 (LLM 기반 동적 생성) */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e9ecef',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* 헤더 */}
                <div style={{
                  marginBottom: '20px',
                  minHeight: '48px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start'
                }}>
                  <div style={{
                    fontSize: '16px',
                    color: '#212529',
                    fontWeight: '700',
                    marginBottom: '6px',
                    lineHeight: '1.3'
                  }}>
                    비즈니스 임팩트
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#868e96',
                    fontWeight: '400',
                    lineHeight: '1.4'
                  }}>
                    LLM 기반 동적 생성
                  </div>
                </div>

                {/* 로딩 상태 */}
                {isLoadingInsight && (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    <div style={{ fontSize: '15px' }}>비즈니스 인사이트를 생성하고 있습니다...</div>
                  </div>
                )}

                {/* LLM 생성 인사이트 표시 */}
                {!isLoadingInsight && businessInsight && (
                  <div style={{ fontSize: '15px', color: '#495057', lineHeight: 1.8 }}>
                    {/* 가장 시급한 개선 대상 */}
                    <div style={{ marginBottom: '20px' }}>
                      <strong style={{ color: '#e74c3c', display: 'block', marginBottom: '10px', fontSize: '16px' }}>
                        가장 시급한 개선 대상: {businessInsight.urgentTarget?.url ? getUrlLabel(businessInsight.urgentTarget.url) : businessInsight.urgentTarget?.site}
                      </strong>
                      <p style={{ margin: 0, color: '#6c757d', lineHeight: 1.7, fontSize: '15px' }}>
                        {businessInsight.urgentTarget?.reason}
                      </p>
                    </div>

                    {/* 예상 비즈니스 영향 */}
                    <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
                      <strong style={{ color: '#2c3e50', display: 'block', marginBottom: '12px', fontSize: '16px' }}>
                        예상 비즈니스 영향
                      </strong>
                      {businessInsight.businessImpact?.summary && (
                        <p style={{ margin: '0 0 14px 0', fontSize: '15px', color: '#495057', lineHeight: 1.7 }}>
                          {businessInsight.businessImpact.summary}
                        </p>
                      )}
                      <div style={{ display: 'grid', gap: '10px' }}>
                        {/* 새 구조 (업계 연구 기반) */}
                        {businessInsight.businessImpact?.conversionRate && typeof businessInsight.businessImpact.conversionRate === 'object' ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px' }}>
                              <span style={{ color: '#27ae60', fontSize: '18px', fontWeight: 'bold' }}>↑</span>
                              <span>전환율: <strong style={{ color: '#27ae60' }}>{businessInsight.businessImpact.conversionRate.range}</strong> {businessInsight.businessImpact.conversionRate.direction}</span>
                            </div>
                            <div style={{ fontSize: '13px', color: '#868e96', marginLeft: '26px', lineHeight: 1.6 }}>
                              {businessInsight.businessImpact.conversionRate.basis}
                            </div>
                          </>
                        ) : (
                          /* 구 구조 (호환성) */
                          businessInsight.businessImpact?.conversionIncrease && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px' }}>
                              <span style={{ color: '#27ae60', fontSize: '18px', fontWeight: 'bold' }}>↑</span>
                              <span>전환율 증가: <strong style={{ color: '#27ae60' }}>{businessInsight.businessImpact.conversionIncrease}</strong></span>
                            </div>
                          )
                        )}

                        {businessInsight.businessImpact?.bounceRate && typeof businessInsight.businessImpact.bounceRate === 'object' ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px' }}>
                              <span style={{ color: '#3498db', fontSize: '18px', fontWeight: 'bold' }}>↓</span>
                              <span>이탈률: <strong style={{ color: '#3498db' }}>{businessInsight.businessImpact.bounceRate.range}</strong> {businessInsight.businessImpact.bounceRate.direction}</span>
                            </div>
                            <div style={{ fontSize: '13px', color: '#868e96', marginLeft: '26px', lineHeight: 1.6 }}>
                              {businessInsight.businessImpact.bounceRate.basis}
                            </div>
                          </>
                        ) : (
                          /* 구 구조 (호환성) */
                          businessInsight.businessImpact?.bounceRateDecrease && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px' }}>
                              <span style={{ color: '#3498db', fontSize: '18px', fontWeight: 'bold' }}>↓</span>
                              <span>이탈률 감소: <strong style={{ color: '#3498db' }}>{businessInsight.businessImpact.bounceRateDecrease}</strong></span>
                            </div>
                          )
                        )}

                        {/* 참고 노트 */}
                        {businessInsight.businessImpact?.note && (
                          <div style={{ padding: '10px', background: 'white', borderRadius: '6px', marginTop: '6px' }}>
                            <span style={{ fontSize: '13px', color: '#6c757d', fontStyle: 'italic', lineHeight: 1.6 }}>
                              💡 {businessInsight.businessImpact.note}
                            </span>
                          </div>
                        )}

                        {/* ROI (구 구조 호환) */}
                        {businessInsight.businessImpact?.roi && (
                          <div style={{ padding: '10px', background: 'white', borderRadius: '6px', marginTop: '6px' }}>
                            <span style={{ fontSize: '14px', color: '#495057', lineHeight: 1.6 }}>
                              {businessInsight.businessImpact.roi}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 핵심 액션 아이템 */}
                    <div>
                      <strong style={{ color: '#2c3e50', display: 'block', marginBottom: '12px', fontSize: '16px' }}>
                        우선순위 개선 작업
                      </strong>
                      <div style={{ display: 'grid', gap: '10px' }}>
                        {businessInsight.actionItems?.map((item: string, idx: number) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '12px',
                              padding: '12px',
                              background: '#f0f9ff',
                              borderRadius: '6px',
                              border: '1px solid #bae6fd'
                            }}
                          >
                            <span style={{
                              background: '#667eea',
                              color: 'white',
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '13px',
                              fontWeight: 'bold',
                              flexShrink: 0
                            }}>
                              {idx + 1}
                            </span>
                            <span style={{ fontSize: '14px', color: '#2c3e50', lineHeight: 1.7 }}>
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* 폴백 UI (에러 또는 조건 미충족) */}
                {!isLoadingInsight && !businessInsight && (
                  <div style={{ fontSize: '15px', color: '#6c757d', lineHeight: 1.8, textAlign: 'center', padding: '20px' }}>
                    <p style={{ margin: 0 }}>
                      비즈니스 인사이트를 생성할 수 없습니다.<br />
                      <span style={{ fontSize: '14px', color: '#868e96' }}>
                        (최소 3개 이상의 사이트 분석이 필요합니다)
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* [B-3-2] 모바일 대응 */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e9ecef',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '300px'
              }}>
                {/* 헤더 */}
                <div style={{
                  marginBottom: '20px',
                  minHeight: '48px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start'
                }}>
                  <div style={{
                    fontSize: '16px',
                    color: '#212529',
                    fontWeight: '700',
                    marginBottom: '6px',
                    lineHeight: '1.3'
                  }}>
                    모바일 대응
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#868e96',
                    fontWeight: '400',
                    lineHeight: '1.4'
                  }}>
                    모바일 트래픽 증가 추세
                  </div>
                </div>

                {/* 점수 섹션 */}
                <div style={{
                  marginBottom: '20px',
                  paddingBottom: '20px',
                  borderBottom: '1px solid #e9ecef',
                  minHeight: '100px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start'
                }}>
                  <div style={{
                    fontSize: '56px',
                    fontWeight: '700',
                    lineHeight: '1',
                    letterSpacing: '-1px',
                    color: '#ff6b6b',
                    textAlign: 'center'
                  }}>
                    20-30%↓
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#868e96',
                    fontWeight: '400',
                    marginTop: '8px',
                    textAlign: 'center'
                  }}>
                    PC 대비 성능 저하 예상
                  </div>
                </div>

                {/* 콘텐츠 */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: '#868e96',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    권장 사항
                  </div>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#495057',
                    lineHeight: '1.6'
                  }}>
                    <strong style={{ display: 'block', marginBottom: '4px' }}>모바일 최적화 병행 필수</strong>
                    모바일 트래픽 비중이 지속 증가하고 있습니다
                  </div>
                </div>
              </div>

            </div>

            {(() => {
              const stats = calculateOverallStats();
              // 커머스 업계 평균 기준 (현실적인 데이터)
              const industryAvg = {
                score: 70,  // 커머스 업계 평균 점수
                fcp: ECOMMERCE_THRESHOLD.FCP_GOOD,  // 3.0초
                lcp: ECOMMERCE_THRESHOLD.LCP_GOOD,  // 4.0초
                tbt: ECOMMERCE_THRESHOLD.TBT_GOOD   // 500ms
              };

              // 차이 계산
              const scoreDiff = stats.avgScore - industryAvg.score;
              const fcpDiff = stats.avgFcp - industryAvg.fcp;
              const lcpDiff = stats.avgLcp - industryAvg.lcp;
              const tbtDiff = stats.avgTbt - industryAvg.tbt;

              // 평가 함수
              const getEvaluation = (diff: number, isLowerBetter: boolean) => {
                const isBetter = isLowerBetter ? diff < 0 : diff > 0;
                const isSlightlyWorse = Math.abs(diff) < (isLowerBetter ? 0.5 : 5);
                if (isBetter) return { label: '우수', bg: '#d4edda', color: '#155724' };
                if (isSlightlyWorse) return { label: '양호', bg: '#d1ecf1', color: '#0c5460' };
                return { label: '개선 필요', bg: '#fff3cd', color: '#856404' };
              };

              const scoreEval = getEvaluation(scoreDiff, false);
              const fcpEval = getEvaluation(fcpDiff, true);
              const lcpEval = getEvaluation(lcpDiff, true);
              const tbtEval = getEvaluation(tbtDiff, true);

              // 통일된 카드 스타일 적용
              const cardStyle = {
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e9ecef',
                display: 'flex' as const,
                flexDirection: 'column' as const,
                marginTop: '30px'
              };

              const cardHeaderStyle = {
                marginBottom: '20px',
                minHeight: '48px',
                display: 'flex' as const,
                flexDirection: 'column' as const,
                justifyContent: 'flex-start' as const
              };

              return (
                <div style={cardStyle}>
                  {/* 카드 헤더 */}
                  <div style={cardHeaderStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#212529', lineHeight: 1.3 }}>
                        커머스 업계 평균 비교 분석
                      </div>
                      {/* 물음표 도움말 아이콘 */}
                      <div
                        title="Google이 권장하는 이상적 기준(FCP 1.8초, LCP 2.5초)은 일반 웹사이트 기준입니다.&#10;&#10;커머스(쇼핑몰) 사이트는 상품 이미지, 리뷰, 추천 시스템 등 필수 콘텐츠가 많아&#10;현실적으로 이 기준을 달성하기 어렵습니다.&#10;&#10;따라서 커머스 특성을 반영한 현실적인 기준을 적용합니다."
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          background: '#e9ecef',
                          color: '#6c757d',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'help'
                        }}
                      >
                        ?
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: '400', color: '#868e96', marginTop: '6px' }}>
                      커머스 현실 기준 적용 - FCP {ECOMMERCE_THRESHOLD.FCP_GOOD}초, LCP {ECOMMERCE_THRESHOLD.LCP_GOOD}초, TBT {ECOMMERCE_THRESHOLD.TBT_GOOD}ms
                    </div>
                  </div>

                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                        <th style={{ padding: '14px', textAlign: 'center', borderRadius: '8px 0 0 0' }}>구분</th>
                        <th style={{ padding: '14px', textAlign: 'center' }}>현재 평균</th>
                        <th style={{ padding: '14px', textAlign: 'center' }}>커머스 업계 기준</th>
                        <th style={{ padding: '14px', textAlign: 'center' }}>차이</th>
                        <th style={{ padding: '14px', textAlign: 'center', borderRadius: '0 8px 0 0' }}>평가</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <strong style={{ fontSize: '14px', color: '#2c3e50' }}>평균 성능 점수</strong>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#667eea' }}>{stats.avgScore.toFixed(1)}점</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#95a5a6' }}>{industryAvg.score}점</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ color: scoreDiff >= 0 ? '#27ae60' : '#e74c3c', fontWeight: '600', fontSize: '16px' }}>{scoreDiff >= 0 ? '+' : ''}{scoreDiff.toFixed(1)}점</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ background: scoreEval.bg, color: scoreEval.color, padding: '6px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>{scoreEval.label}</span>
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <strong style={{ fontSize: '14px', color: '#2c3e50' }}>평균 FCP</strong>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ fontSize: '18px', fontWeight: 'bold', color: stats.avgFcp <= industryAvg.fcp ? '#27ae60' : '#f39c12' }}>{stats.avgFcp.toFixed(1)}초</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#95a5a6' }}>{industryAvg.fcp}초</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ color: fcpDiff <= 0 ? '#27ae60' : '#e74c3c', fontWeight: '600', fontSize: '16px' }}>{fcpDiff >= 0 ? '+' : ''}{fcpDiff.toFixed(1)}초</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ background: fcpEval.bg, color: fcpEval.color, padding: '6px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>{fcpEval.label}</span>
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <strong style={{ fontSize: '14px', color: '#2c3e50' }}>평균 LCP</strong>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ fontSize: '18px', fontWeight: 'bold', color: stats.avgLcp <= industryAvg.lcp ? '#27ae60' : '#f39c12' }}>{stats.avgLcp.toFixed(1)}초</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#95a5a6' }}>{industryAvg.lcp}초</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ color: lcpDiff <= 0 ? '#27ae60' : '#e74c3c', fontWeight: '600', fontSize: '16px' }}>{lcpDiff >= 0 ? '+' : ''}{lcpDiff.toFixed(1)}초</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ background: lcpEval.bg, color: lcpEval.color, padding: '6px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>{lcpEval.label}</span>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <strong style={{ fontSize: '14px', color: '#2c3e50' }}>평균 TBT</strong>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ fontSize: '18px', fontWeight: 'bold', color: stats.avgTbt <= industryAvg.tbt ? '#27ae60' : '#e74c3c' }}>{Math.round(stats.avgTbt)}ms</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#95a5a6' }}>{industryAvg.tbt}ms</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ color: tbtDiff <= 0 ? '#27ae60' : '#e74c3c', fontWeight: '600', fontSize: '16px' }}>{tbtDiff >= 0 ? '+' : ''}{Math.round(tbtDiff)}ms</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ background: tbtEval.bg, color: tbtEval.color, padding: '6px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>{tbtEval.label}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* 기준 설명 도움말 박스 */}
                  <div style={{
                    marginTop: '20px',
                    padding: '16px 20px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: '12px',
                    border: '1px dashed #ced4da'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{
                        flexShrink: 0,
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#667eea',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700'
                      }}>
                        ?
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#495057', marginBottom: '8px' }}>
                          "커머스 업계 기준"이란?
                        </div>
                        <div style={{ fontSize: '13px', color: '#6c757d', lineHeight: 1.7 }}>
                          <p style={{ margin: '0 0 8px 0' }}>
                            <strong style={{ color: '#495057' }}>Google 이상적 기준</strong>: FCP 1.8초, LCP 2.5초, TBT 200ms
                            <br />
                            <span style={{ fontSize: '12px', color: '#868e96' }}>→ 일반 웹사이트(블로그, 뉴스 등) 기준으로 커머스에는 비현실적</span>
                          </p>
                          <p style={{ margin: 0 }}>
                            <strong style={{ color: '#495057' }}>커머스 현실 기준</strong>: FCP {ECOMMERCE_THRESHOLD.FCP_GOOD}초, LCP {ECOMMERCE_THRESHOLD.LCP_GOOD}초, TBT {ECOMMERCE_THRESHOLD.TBT_GOOD}ms
                            <br />
                            <span style={{ fontSize: '12px', color: '#868e96' }}>→ 국내 주요 쇼핑몰 실측 데이터 및 업계 평균을 기반으로 설정한 현실적인 기준</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 컨텐츠 영역: 분석 요약 */}
                  <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e9ecef' }}>
                    <div style={{ fontSize: '14px', color: '#495057', lineHeight: 1.8 }}>
                      <strong style={{ color: '#212529', fontSize: '14px' }}>분석 요약:</strong>{' '}
                      {stats.avgFcp <= industryAvg.fcp ? 'FCP는 커머스 업계 기준 대비 우수합니다. ' : 'FCP 개선이 필요합니다. '}
                      {stats.avgLcp <= industryAvg.lcp ? 'LCP도 양호한 수준입니다. ' : 'LCP 최적화가 권장됩니다. '}
                      {stats.avgTbt <= industryAvg.tbt ? 'TBT는 적정 범위 내입니다.' : 'TBT 개선을 통해 인터랙션 반응성을 높일 수 있습니다.'}
                    </div>
                  </div>
                </div>
              );
            })()}

          </div>
          </div>
        </div>
        {/* ========== 종합 분석 그리드 끝 ========== */}

        <div style={{ padding: '2rem 5%', background: '#ffffff' }}>
          {/* 통일된 카드 스타일 적용 */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e9ecef',
            display: 'flex',
            flexDirection: 'column' as const,
            overflowX: 'auto'
          }}>
            {/* 카드 헤더 */}
            <div style={{
              marginBottom: '20px',
              minHeight: '48px',
              display: 'flex',
              flexDirection: 'column' as const,
              justifyContent: 'flex-start'
            }}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#212529', lineHeight: 1.3 }}>
                페이지별 종합 성능 지표
              </div>
              <div style={{ fontSize: '13px', fontWeight: '400', color: '#868e96', marginTop: '6px' }}>
                각 페이지의 핵심 점수, 디바이스별 메트릭, 종합 건강도를 한눈에 비교
              </div>
            </div>

            {/* 테이블 */}
            <table style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              fontSize: '14px'
            }}>
            <thead style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <tr>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', borderTopLeftRadius: '8px' }}>서버명 / 페이지</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>핵심 점수<br /><small style={{ fontSize: '11px' }}>(0-100)</small></th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>디바이스</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>FCP<br /><small style={{ fontSize: '11px' }}>(첫 콘텐츠 표시)</small></th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>LCP<br /><small style={{ fontSize: '11px' }}>(최대 콘텐츠 표시)</small></th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>TBT<br /><small style={{ fontSize: '11px' }}>(총 차단 시간)</small></th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>종합 건강도</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', borderTopRightRadius: '8px' }}>상태</th>
              </tr>
            </thead>
            <tbody>
              {/* 실제 데이터로 동적 렌더링 */}
              {validResults.map((result, index) => {
                const device = selectedDevicePerUrl[result.url] || 'mobile';
                const pageSpeedData = device === 'mobile' ? result.pageSpeedData?.mobile : result.pageSpeedData?.desktop;
                const score = device === 'mobile' ? (result.customRatings?.mobile || 0) : (result.customRatings?.desktop || 0);

                const fcp = pageSpeedData?.firstContentfulPaint || 0;
                const lcp = pageSpeedData?.largestContentfulPaint || 0;
                const tbt = pageSpeedData?.totalBlockingTime || 0;

                const fcpStyle = getMetricStyle(fcp, 'fcp');
                const lcpStyle = getMetricStyle(lcp, 'lcp');
                const tbtStyle = getMetricStyle(tbt, 'tbt');
                const statusInfo = getStatusInfo(score);

                return (
                  <tr key={result.url} style={{ borderBottom: '1px solid #e9ecef' }}>
                    {/* 서버명/페이지 */}
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>🌐</span>
                        <span>{getUrlLabel(result.url)}</span>
                      </div>
                    </td>

                    {/* 핵심 점수 */}
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        margin: '0 auto',
                        background: getScoreBadgeGradient(score),
                        color: 'white'
                      }}>{Math.round(score)}</div>
                    </td>

                    {/* 디바이스 토글 */}
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <div style={{
                        display: 'inline-flex',
                        background: '#e9ecef',
                        borderRadius: '20px',
                        padding: '2px',
                        gap: '2px'
                      }}>
                        <button
                          onClick={() => toggleDevice(result.url, 'desktop')}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '18px',
                            border: 'none',
                            background: device === 'desktop' ? 'white' : 'transparent',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: device === 'desktop' ? '#667eea' : '#6c757d',
                            boxShadow: device === 'desktop' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                          }}>💻 PC</button>
                        <button
                          onClick={() => toggleDevice(result.url, 'mobile')}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '18px',
                            border: 'none',
                            background: device === 'mobile' ? 'white' : 'transparent',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: device === 'mobile' ? '#667eea' : '#6c757d',
                            boxShadow: device === 'mobile' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                          }}>📱 Mobile</button>
                      </div>
                    </td>

                    {/* FCP */}
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '6px 12px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: fcpStyle.bgColor,
                        color: fcpStyle.textColor
                      }}>{fcp.toFixed(1)}초</span>
                    </td>

                    {/* LCP */}
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '6px 12px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: lcpStyle.bgColor,
                        color: lcpStyle.textColor
                      }}>{lcp.toFixed(1)}초</span>
                    </td>

                    {/* TBT */}
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '6px 12px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: tbtStyle.bgColor,
                        color: tbtStyle.textColor
                      }}>{Math.round(tbt)}ms</span>
                    </td>

                    {/* 종합 건강도 */}
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ padding: '0 20px' }}>
                        <strong>{Math.round(score)}%</strong>
                        <div style={{ width: '100%', height: '8px', background: '#e9ecef', borderRadius: '4px', overflow: 'hidden', marginTop: '6px' }}>
                          <div style={{
                            height: '100%',
                            borderRadius: '4px',
                            width: `${score}%`,
                            background: getScoreBadgeGradient(score)
                          }}></div>
                        </div>
                      </div>
                    </td>

                    {/* 상태 */}
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '600',
                        background: statusInfo.bgColor,
                        color: statusInfo.color
                      }}>{statusInfo.emoji} {statusInfo.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

        <div style={{ padding: '2rem 5%', background: '#ffffff' }}>
          {/* 섹션 헤더 (통일된 스타일) */}
          <div style={{
            marginBottom: '24px',
            minHeight: '48px',
            display: 'flex',
            flexDirection: 'column' as const,
            justifyContent: 'flex-start'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#212529', lineHeight: 1.3 }}>
              페이지별 상세 분석 및 개선 방안
            </div>
            <div style={{ fontSize: '14px', fontWeight: '400', color: '#868e96', marginTop: '6px' }}>
              각 페이지의 문제점, 비즈니스 임팩트, AI 기반 개선 권장사항을 확인하세요
            </div>
          </div>

          {/* 동적 렌더링: validResults 기반 - zSectionDevice에 따라 데이터 선택 */}
          {validResults.map((result, index) => {
            // zSectionDevice에 따라 점수와 PageSpeed 데이터 선택
            const score = zSectionDevice === 'mobile'
              ? (result.customRatings?.mobile || result.customRatings?.overall || 0)
              : (result.customRatings?.desktop || result.customRatings?.overall || 0);
            const pageSpeedData = zSectionDevice === 'mobile'
              ? result.pageSpeedData?.mobile
              : result.pageSpeedData?.desktop;
            const statusInfo = getStatusInfo(score);
            const issues = generateIssues(pageSpeedData);
            const recommendations = generateRecommendations(pageSpeedData);

            // 통일된 카드 스타일
            const cardStyle = {
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              border: '1px solid #e9ecef',
              display: 'flex' as const,
              flexDirection: 'column' as const,
              marginBottom: '32px'
            };

            const cardHeaderStyle = {
              marginBottom: '20px',
              paddingBottom: '20px',
              borderBottom: '1px solid #e9ecef',
              minHeight: '48px',
              display: 'flex' as const,
              justifyContent: 'space-between' as const,
              alignItems: 'center' as const
            };

            return (
              <React.Fragment key={result.url}>
                <div style={cardStyle}>
                  {/* 카드 헤더 */}
                  <div style={cardHeaderStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '16px', fontWeight: '700', color: '#212529' }}>{getUrlLabel(result.url)}</span>
                      <span style={{
                        display: 'inline-block',
                        padding: '6px 14px',
                        borderRadius: '16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        background: statusInfo.bgColor,
                        color: statusInfo.color
                      }}>{statusInfo.emoji} {statusInfo.label}</span>
                    </div>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      fontWeight: 'bold',
                      fontSize: '20px',
                      background: getScoreBadgeGradient(score),
                      color: 'white'
                    }}>{Math.round(score)}</div>
                  </div>

                  {/* AI 기반 개선 버전 */}
                  <div style={{ padding: '24px' }}>
                      {isLoadingPageInsights[result.url] ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          <div style={{ fontSize: '16px', marginBottom: '12px' }}>AI가 실제 PageSpeed 데이터를 분석하고 있습니다...</div>
                          <div style={{ fontSize: '14px', color: '#999' }}>잠시만 기다려주세요 (15-30초 소요)</div>
                        </div>
                      ) : getImprovedInsight(result.url) ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
                          {/* 왼쪽: 문제점 + 비즈니스 임팩트 */}
                          <div style={{ display: 'grid', gap: '20px' }}>
                            {/* 주요 문제점 */}
                            <div>
                            <h4 style={{
                              fontSize: '18px',
                              fontWeight: '600',
                              color: '#1a202c',
                              marginBottom: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px'
                            }}>
                              <span style={{ fontSize: '20px' }}>🔍</span>
                              주요 문제점
                            </h4>
                            <div style={{ display: 'grid', gap: '12px' }}>
                              {getImprovedInsight(result.url).insights.issues?.map((issue: any, idx: number) => (
                                <div key={idx} style={{
                                  background: 'white',
                                  border: `1px solid ${issue.severity === 'high' ? '#f87171' : issue.severity === 'medium' ? '#fb923c' : '#60a5fa'}`,
                                  borderLeft: `4px solid ${issue.severity === 'high' ? '#ef4444' : issue.severity === 'medium' ? '#f97316' : '#3b82f6'}`,
                                  padding: '16px 20px',
                                  borderRadius: '8px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}>
                                  <div style={{ fontWeight: '600', marginBottom: '8px', color: '#1a202c', fontSize: '15px' }}>
                                    {issue.title}
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6 }}>
                                    {issue.description}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* 비즈니스 임팩트 */}
                          <div>
                            <h4 style={{
                              fontSize: '18px',
                              fontWeight: '600',
                              color: '#1a202c',
                              marginBottom: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px'
                            }}>
                              <span style={{ fontSize: '20px' }}>💼</span>
                              비즈니스 임팩트
                            </h4>
                            <div style={{
                              background: 'white',
                              padding: '20px 24px',
                              borderRadius: '12px',
                              border: '1px solid #e5e7eb',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                              {/* 사용자 경험 */}
                              <div style={{
                                fontSize: '14px',
                                marginBottom: '18px',
                                padding: '14px',
                                background: '#f9fafb',
                                borderRadius: '8px',
                                lineHeight: 1.7,
                                color: '#374151'
                              }}>
                                <strong style={{ color: '#1f2937', display: 'block', marginBottom: '6px' }}>사용자 경험:</strong>
                                {getImprovedInsight(result.url).insights.impact?.userExperience}
                              </div>

                              {/* 예상 효과 */}
                              <div>
                                <strong style={{ color: '#1f2937', fontSize: '14px', display: 'block', marginBottom: '12px' }}>
                                  예상 효과 (업계 연구 기준)
                                </strong>
                                {typeof getImprovedInsight(result.url).insights.impact?.businessMetrics === 'object' ? (
                                  <div style={{ display: 'grid', gap: '10px' }}>
                                    {getImprovedInsight(result.url).insights.impact.businessMetrics.conversionRate && (
                                      <div style={{
                                        padding: '14px 16px',
                                        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                                        borderRadius: '8px',
                                        border: '1px solid #86efac'
                                      }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                          <span style={{ fontSize: '18px' }}>📈</span>
                                          <span style={{ fontWeight: '600', color: '#059669' }}>전환율 개선</span>
                                        </div>
                                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#047857' }}>
                                          {getImprovedInsight(result.url).insights.impact.businessMetrics.conversionRate}
                                        </div>
                                        {getImprovedInsight(result.url).insights.impact.businessMetrics.note && (
                                          <div style={{ fontSize: '12px', color: '#059669', marginTop: '4px' }}>
                                            {getImprovedInsight(result.url).insights.impact.businessMetrics.note}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {getImprovedInsight(result.url).insights.impact.businessMetrics.bounceRate && (
                                      <div style={{
                                        padding: '14px 16px',
                                        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                        borderRadius: '8px',
                                        border: '1px solid #93c5fd'
                                      }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                          <span style={{ fontSize: '18px' }}>📉</span>
                                          <span style={{ fontWeight: '600', color: '#0284c7' }}>이탈률 감소</span>
                                        </div>
                                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#0369a1' }}>
                                          {getImprovedInsight(result.url).insights.impact.businessMetrics.bounceRate}
                                        </div>
                                        {getImprovedInsight(result.url).insights.impact.businessMetrics.note && (
                                          <div style={{ fontSize: '12px', color: '#0284c7', marginTop: '4px' }}>
                                            {getImprovedInsight(result.url).insights.impact.businessMetrics.note}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div style={{
                                    color: '#4b5563',
                                    padding: '12px',
                                    background: '#f9fafb',
                                    borderRadius: '8px'
                                  }}>
                                    {getImprovedInsight(result.url).insights.impact?.businessMetrics}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          </div>

                          {/* 오른쪽: 개선 작업 로드맵 */}
                          <div style={{
                            background: '#f8f9fa',
                            padding: '20px',
                            borderRadius: '8px',
                            borderLeft: '4px solid #2467E7'
                          }}>
                            <h4 style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#2c3e50',
                              marginBottom: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              ⚡ 개선 작업
                            </h4>
                            <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.8' }}>
                              {getImprovedInsight(result.url).insights.recommendations?.sort((a: any, b: any) => a.priority - b.priority).map((rec: any, idx: number) => (
                                <div key={idx} style={{
                                  marginBottom: idx < getImprovedInsight(result.url).insights.recommendations.length - 1 ? '16px' : '0',
                                  paddingBottom: idx < getImprovedInsight(result.url).insights.recommendations.length - 1 ? '16px' : '0',
                                  borderBottom: idx < getImprovedInsight(result.url).insights.recommendations.length - 1 ? '1px solid #dee2e6' : 'none'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' }}>
                                    <span style={{
                                      background: rec.priority === 1 ? '#ef4444' : rec.priority === 2 ? '#f97316' : rec.priority === 3 ? '#3b82f6' : '#6b7280',
                                      color: 'white',
                                      minWidth: '22px',
                                      height: '22px',
                                      borderRadius: '4px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: '700',
                                      fontSize: '12px',
                                      flexShrink: 0
                                    }}>{rec.priority}</span>
                                    <strong style={{ fontSize: '14px', color: '#1f2937', lineHeight: 1.3 }}>
                                      {rec.title}
                                    </strong>
                                  </div>
                                  <div style={{
                                    fontSize: '13px',
                                    marginBottom: '8px',
                                    color: '#4b5563',
                                    lineHeight: 1.5
                                  }}>
                                    {rec.description}
                                  </div>
                                  <div style={{
                                    padding: '6px 10px',
                                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                                    borderRadius: '6px',
                                    border: '1px solid #86efac',
                                    display: 'inline-block'
                                  }}>
                                    <span style={{
                                      fontSize: '13px',
                                      color: '#047857',
                                      fontWeight: '600'
                                    }}>
                                      예상 절감: {rec.expectedSavings}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                          개선 버전 데이터를 불러올 수 없습니다.
                        </div>
                      )}
                    </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/*
          [G] 우선순위별 개선 과제 - 현재 사용하지 않음 (2025.11.26 주석처리)
          나중에 필요시 주석 해제하여 사용 가능

        <div style={{ padding: '2rem 5%', background: '#f8f9fa' }}>
          <div style={indexLabelStyle}>[G]</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <span style={{ fontSize: '28px' }}>🎯</span>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#2c3e50', margin: 0 }}>우선순위별 개선 과제</h2>
          </div>

          <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <th style={{ padding: '14px', textAlign: 'center', width: '100px', borderRadius: '8px 0 0 0' }}>우선순위</th>
                  <th style={{ padding: '14px', textAlign: 'left' }}>개선 과제</th>
                  <th style={{ padding: '14px', textAlign: 'left' }}>세부 내용</th>
                  <th style={{ padding: '14px', textAlign: 'center', width: '120px', borderRadius: '0 8px 0 0' }}>예상 효과</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{ background: '#e74c3c', color: 'white', padding: '6px 14px', borderRadius: '16px', fontSize: '13px', fontWeight: '600' }}>긴급</span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <strong style={{ fontSize: '15px', color: '#2c3e50' }}>상품 상세 페이지 성능 개선</strong>
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#666' }}>
                    LCP 3.9초 → 2.5초 이하 목표
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{ color: '#27ae60', fontWeight: '600', fontSize: '14px' }}>+30-40%</span>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{ background: '#f39c12', color: 'white', padding: '6px 14px', borderRadius: '16px', fontSize: '13px', fontWeight: '600' }}>중요</span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <strong style={{ fontSize: '15px', color: '#2c3e50' }}>이미지 최적화 전략 수립</strong>
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#666' }}>
                    WebP 포맷 전환, Lazy Loading 적용
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{ color: '#27ae60', fontWeight: '600', fontSize: '14px' }}>+20-30%</span>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{ background: '#f39c12', color: 'white', padding: '6px 14px', borderRadius: '16px', fontSize: '13px', fontWeight: '600' }}>중요</span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <strong style={{ fontSize: '15px', color: '#2c3e50' }}>JavaScript 번들 크기 최적화</strong>
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#666' }}>
                    코드 스플리팅, Tree Shaking 적용
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{ color: '#27ae60', fontWeight: '600', fontSize: '14px' }}>+15-25%</span>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{ background: '#3498db', color: 'white', padding: '6px 14px', borderRadius: '16px', fontSize: '13px', fontWeight: '600' }}>권장</span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <strong style={{ fontSize: '15px', color: '#2c3e50' }}>모바일 성능 테스트 및 최적화</strong>
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#666' }}>
                    반응형 이미지, 터치 최적화
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{ color: '#3498db', fontWeight: '600', fontSize: '14px' }}>+10-20%</span>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{ background: '#3498db', color: 'white', padding: '6px 14px', borderRadius: '16px', fontSize: '13px', fontWeight: '600' }}>권장</span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <strong style={{ fontSize: '15px', color: '#2c3e50' }}>CDN 및 캐싱 전략 강화</strong>
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#666' }}>
                    정적 리소스 캐싱 기간 연장
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{ color: '#3498db', fontWeight: '600', fontSize: '14px' }}>+5-15%</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        */}

        {/* [H] 최종 종합 마무리 - 실제 데이터 기반 */}
        {(() => {
          const stats = calculateOverallStats();
          const avgScore = Math.round(stats.avgScore);
          const worstPageLabel = getUrlLabel(stats.worstResult.url);
          const bestPageLabel = getUrlLabel(stats.bestResult.url);
          const bestScore = Math.round(stats.bestResult.customRatings?.overall || stats.bestResult.customRatings?.mobile || 0);
          const worstScore = Math.round(stats.worstResult.customRatings?.overall || stats.worstResult.customRatings?.mobile || 0);

          // 실제 문제점 기반 개선 과제 생성 (파일 상단 ECOMMERCE_THRESHOLD 상수 사용)
          const actionItems: { priority: string; task: string; detail: string; color: string }[] = [];

          // LCP 분석 (커머스 기준)
          if (stats.avgLcp > ECOMMERCE_THRESHOLD.LCP_CRITICAL) {
            actionItems.push({
              priority: '긴급',
              task: 'LCP(최대 콘텐츠 표시) 심각',
              detail: `현재 평균 ${stats.avgLcp.toFixed(1)}초 → 메인 배너 이미지 용량 축소, 지연 로딩 적용 필요`,
              color: '#e74c3c'
            });
          } else if (stats.avgLcp > ECOMMERCE_THRESHOLD.LCP_WARNING) {
            actionItems.push({
              priority: '권장',
              task: 'LCP(최대 콘텐츠 표시) 개선 가능',
              detail: `현재 평균 ${stats.avgLcp.toFixed(1)}초 (커머스 평균 수준) → 이미지 포맷 최적화로 개선 여지 있음`,
              color: '#3498db'
            });
          }

          // TBT 분석 (커머스 기준 - JS 많이 사용)
          if (stats.avgTbt > ECOMMERCE_THRESHOLD.TBT_CRITICAL) {
            actionItems.push({
              priority: '긴급',
              task: 'TBT(총 차단 시간) 심각',
              detail: `현재 평균 ${(stats.avgTbt / 1000).toFixed(1)}초 → 불필요한 서드파티 스크립트 제거, 지연 로딩 적용`,
              color: '#e74c3c'
            });
          } else if (stats.avgTbt > ECOMMERCE_THRESHOLD.TBT_WARNING) {
            actionItems.push({
              priority: '권장',
              task: 'TBT(총 차단 시간) 개선 가능',
              detail: `현재 평균 ${(stats.avgTbt / 1000).toFixed(1)}초 (커머스 평균 수준) → 스크립트 비동기 로딩으로 개선 가능`,
              color: '#3498db'
            });
          }

          // FCP 분석 (커머스 기준)
          if (stats.avgFcp > ECOMMERCE_THRESHOLD.FCP_CRITICAL) {
            actionItems.push({
              priority: '중요',
              task: 'FCP(첫 콘텐츠 표시) 느림',
              detail: `현재 평균 ${stats.avgFcp.toFixed(1)}초 → 서버 응답 시간 점검, 렌더링 차단 리소스 확인 필요`,
              color: '#f39c12'
            });
          }

          // 페이지 크기 분석 (커머스 기준 - 이미지 많음)
          const avgSize = validResults.reduce((sum, r) => {
            const size = r.pageSpeedData?.mobile?.pageSize?.totalSize || 0;
            return sum + size;
          }, 0) / validResults.length / 1024 / 1024;

          if (avgSize > ECOMMERCE_THRESHOLD.SIZE_CRITICAL) {
            actionItems.push({
              priority: '긴급',
              task: '페이지 용량 과다',
              detail: `현재 평균 ${avgSize.toFixed(1)}MB → 이미지 품질/해상도 재검토, 불필요한 리소스 제거 시급`,
              color: '#e74c3c'
            });
          } else if (avgSize > ECOMMERCE_THRESHOLD.SIZE_WARNING) {
            actionItems.push({
              priority: '권장',
              task: '페이지 용량 개선 가능',
              detail: `현재 평균 ${avgSize.toFixed(1)}MB (커머스 평균 수준) → WebP 포맷 전환으로 20-30% 절감 가능`,
              color: '#3498db'
            });
          }

          // 모든 지표가 양호한 경우
          if (actionItems.length === 0) {
            actionItems.push({
              priority: '양호',
              task: '현재 성능 상태 우수',
              detail: '커머스 사이트 기준 양호한 성능 → 현재 상태 유지 및 정기적인 모니터링 권장',
              color: '#27ae60'
            });
          }

          return (
            <div style={{ padding: '2rem 5%', background: '#ffffff' }}>
              <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                  <h2 style={{ fontSize: '26px', fontWeight: '700', color: '#2c3e50', marginBottom: '12px' }}>분석 결과 요약 및 개선 방향</h2>
                  <p style={{ fontSize: '15px', color: '#666' }}>총 {validResults.length}개 페이지 분석 결과 기반</p>
                </div>

                {/* 현재 상태 요약 */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '32px' }}>
                  <div style={{ background: '#f8f9fa', padding: '28px', borderRadius: '12px', border: '2px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '12px' }}>평균 성능 점수</div>
                    <div style={{ fontSize: '36px', fontWeight: '800', color: avgScore >= 70 ? '#27ae60' : avgScore >= 50 ? '#f39c12' : '#e74c3c' }}>
                      {avgScore}점
                    </div>
                    <div style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>
                      {avgScore >= 80 ? '우수' : avgScore >= 70 ? '양호' : avgScore >= 50 ? '보통' : '개선 필요'}
                    </div>
                  </div>
                  <div style={{ background: '#f8f9fa', padding: '28px', borderRadius: '12px', border: '2px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '12px' }}>최고 성능 페이지</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#27ae60', marginBottom: '4px' }}>
                      {bestPageLabel}
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#27ae60' }}>
                      {bestScore}점
                    </div>
                  </div>
                  <div style={{ background: '#f8f9fa', padding: '28px', borderRadius: '12px', border: '2px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '12px' }}>개선 필요 페이지</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#e74c3c', marginBottom: '4px' }}>
                      {worstPageLabel}
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#e74c3c' }}>
                      {worstScore}점
                    </div>
                  </div>
                </div>

                {/* 실제 데이터 기반 개선 과제 */}
                <div style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2c3e50', marginBottom: '16px' }}>
                    우선 개선 과제
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {actionItems.map((item, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '12px 16px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${item.color}`
                      }}>
                        <span style={{
                          background: item.color,
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          flexShrink: 0
                        }}>
                          {item.priority}
                        </span>
                        <div>
                          <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '4px' }}>{item.task}</div>
                          <div style={{ fontSize: '13px', color: '#666' }}>{item.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 결론 - 커머스 현실 기준 */}
                <div style={{ background: '#f8f9fa', padding: '32px', borderRadius: '12px', borderLeft: '4px solid #667eea' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#2c3e50', marginBottom: '16px' }}>결론</div>
                  <div style={{ fontSize: '15px', color: '#555', lineHeight: '1.9' }}>
                    분석된 {validResults.length}개 페이지의 <strong style={{ color: '#667eea' }}>평균 성능 점수는 {avgScore}점</strong>입니다.
                    {' '}
                    {avgScore >= 75 ? (
                      <>커머스 사이트 기준으로 <strong style={{ color: '#27ae60' }}>우수한 성능</strong>을 보이고 있습니다. </>
                    ) : avgScore >= 65 ? (
                      <>커머스 사이트 기준으로 <strong style={{ color: '#3498db' }}>양호한 수준</strong>입니다. </>
                    ) : avgScore >= 50 ? (
                      <>커머스 사이트 기준으로 <strong style={{ color: '#f39c12' }}>평균 수준</strong>이며 개선 여지가 있습니다. </>
                    ) : (
                      <>커머스 사이트 기준으로도 <strong style={{ color: '#e74c3c' }}>개선이 필요한 상태</strong>입니다. </>
                    )}
                    {stats.avgLcp > ECOMMERCE_THRESHOLD.LCP_CRITICAL && (
                      <>특히 <strong>LCP {stats.avgLcp.toFixed(1)}초</strong>는 커머스 기준으로도 느린 편이므로 메인 이미지 최적화가 필요합니다. </>
                    )}
                    {stats.avgTbt > ECOMMERCE_THRESHOLD.TBT_CRITICAL && (
                      <><strong>TBT {(stats.avgTbt / 1000).toFixed(1)}초</strong>가 높아 불필요한 스크립트 정리를 권장합니다. </>
                    )}
                    {avgSize > ECOMMERCE_THRESHOLD.SIZE_CRITICAL && (
                      <>페이지 용량 <strong>{avgSize.toFixed(1)}MB</strong>는 과다하므로 이미지 최적화가 시급합니다. </>
                    )}
                    {actionItems.length === 1 && actionItems[0].priority === '양호' && (
                      <>현재 상태를 유지하며 정기적인 모니터링을 권장합니다.</>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '16px', fontStyle: 'italic' }}>
                    * 커머스 사이트는 상품 이미지, 리뷰, 추천 등 필수 콘텐츠가 많아 일반 웹사이트보다 느린 것이 정상입니다.
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  // ============================================================================
  // Case 1 (Default): 기존 VerticalTable + 구조화된 총평
  // ============================================================================
  return (
    <div>
      {/*
        수평 테이블 - 현재 사용하지 않음 (2025.11.25 주석처리)
        나중에 필요시 주석 해제하여 사용 가능

      <VerticalTable
        validResults={validResults}
        selectedUrl={selectedUrl}
        getUrlLabel={getUrlLabel}
        formatTime={formatTime}
        formatSize={formatSize}
        formatTBT={formatTBT}
        getMetricColor={getMetricColor}
        getMetricIcon={getMetricIcon}
        getPageSizeColor={getPageSizeColor}
        getPageSizeIcon={getPageSizeIcon}
        getPageSizeLabel={getPageSizeLabel}
        onUrlClick={onUrlClick}
      />
      */}




      {/* 공통 장점/개선사항/페이지별 특징 */}
      {structuredSummary && (
        <div style={{ marginTop: '2rem' }}>
          {/* 공통 장점 */}
          {structuredSummary.commonStrengths.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{
                fontSize: '1.125rem',
                fontWeight: '600',
                color: designSystem.colors.success,
                marginBottom: '1rem'
              }}>
                공통 장점
              </h4>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {structuredSummary.commonStrengths.map((item, idx) => (
                  <li key={idx} style={{
                    padding: '0.75rem 1rem',
                    marginBottom: '0.5rem',
                    backgroundColor: `${designSystem.colors.success}15`,
                    borderLeft: `4px solid ${designSystem.colors.success}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.95rem',
                    lineHeight: '1.6'
                  }}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 주요 개선사항 */}
          {structuredSummary.commonWeaknesses.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{
                fontSize: '1.125rem',
                fontWeight: '600',
                color: designSystem.colors.warning,
                marginBottom: '1rem'
              }}>
                주요 개선사항
              </h4>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {structuredSummary.commonWeaknesses.map((item, idx) => (
                  <li key={idx} style={{
                    padding: '0.75rem 1rem',
                    marginBottom: '0.5rem',
                    backgroundColor: `${designSystem.colors.warning}15`,
                    borderLeft: `4px solid ${designSystem.colors.warning}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.95rem',
                    lineHeight: '1.6'
                  }}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 페이지별 주요 특징 */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: designSystem.colors.primary,
              marginBottom: '1rem'
            }}>
              페이지별 주요 특징
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '1.5rem'
            }}>
              {structuredSummary.siteInsights.map((site, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#F9FAFB',
                  padding: '1.5rem',
                  borderRadius: '0.75rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  boxShadow: designSystem.shadows.sm
                }}>
                  <h5 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: designSystem.colors.textPrimary,
                    marginBottom: '1rem'
                  }}>
                    {site.siteName}
                  </h5>

                  {/* 강점 */}
                  {site.highlights.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: designSystem.colors.success,
                        marginBottom: '0.5rem'
                      }}>
                        강점
                      </div>
                      <ul style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        fontSize: '0.85rem',
                        lineHeight: '1.6'
                      }}>
                        {site.highlights.map((highlight, hIdx) => (
                          <li key={hIdx} style={{
                            paddingLeft: '1.25rem',
                            position: 'relative' as const,
                            marginBottom: '0.35rem',
                            color: designSystem.colors.textSecondary
                          }}>
                            <span style={{
                              position: 'absolute',
                              left: 0,
                              color: designSystem.colors.success
                            }}>•</span>
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 개선 필요 사항 */}
                  {site.issues.length > 0 && (
                    <div>
                      <div style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: designSystem.colors.warning,
                        marginBottom: '0.5rem'
                      }}>
                        개선 필요
                      </div>
                      <ul style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        fontSize: '0.85rem',
                        lineHeight: '1.6'
                      }}>
                        {site.issues.map((issue, iIdx) => (
                          <li key={iIdx} style={{
                            paddingLeft: '1.25rem',
                            position: 'relative' as const,
                            marginBottom: '0.35rem',
                            color: designSystem.colors.textSecondary
                          }}>
                            <span style={{
                              position: 'absolute',
                              left: 0,
                              color: designSystem.colors.warning
                            }}>•</span>
                            {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

// ============================================================================
// 테이블 컴포넌트 타입 정의
// ============================================================================
interface TableProps {
  validResults: (AnalysisResult & { originalIndex: number })[];
  selectedUrl: string | undefined;
  getUrlLabel: (url: string) => string;
  formatTime: (ms?: number) => string;
  formatSize: (bytes?: number) => string;
  formatTBT: (ms?: number) => string;
  getMetricColor: (metric: 'FCP' | 'LCP' | 'TBT', value?: number) => string;
  getMetricIcon: (metric: 'FCP' | 'LCP' | 'TBT', value?: number) => string;
  getPageSizeColor: (bytes?: number) => string;
  getPageSizeIcon: (bytes?: number) => string;
  getPageSizeLabel: (bytes?: number) => string;
  onUrlClick: (url: string, index: number) => void;
}

const VerticalTable: React.FC<TableProps> = ({
  validResults,
  selectedUrl,
  getUrlLabel,
  formatTime,
  formatSize,
  formatTBT,
  getMetricColor,
  getMetricIcon,
  getPageSizeColor,
  getPageSizeIcon,
  getPageSizeLabel,
  onUrlClick
}) => {
  const [hoveredUrl, setHoveredUrl] = useState<string | null>(null);

  const isActiveRow = (url: string): boolean => {
    return selectedUrl !== undefined && selectedUrl === url;
  };

  return (
    <div style={tableStyles.container}>
      <div style={tableStyles.scrollWrapper}>
        <table style={tableStyles.table}>
          <thead>
            <tr>
              <th style={tableStyles.th}>사이트</th>
              <th style={{ ...tableStyles.th, textAlign: 'center' }}>
                <div>전체 점수</div>
                <div style={{ fontSize: '0.75rem', fontWeight: '400', color: designSystem.colors.textMuted, marginTop: '0.25rem' }}>
                  (종합 평가)
                </div>
              </th>
              <th style={{ ...tableStyles.th, textAlign: 'center' }}>
                <div>PC / 모바일</div>
                <div style={{ fontSize: '0.75rem', fontWeight: '400', color: designSystem.colors.textMuted, marginTop: '0.25rem' }}>
                  (기기별 점수)
                </div>
              </th>
              <th style={{ ...tableStyles.th, textAlign: 'center' }}>
                <div>FCP</div>
                <div style={{ fontSize: '0.75rem', fontWeight: '400', color: designSystem.colors.textMuted, marginTop: '0.25rem' }}>
                  (첫 콘텐츠 표시)
                </div>
              </th>
              <th style={{ ...tableStyles.th, textAlign: 'center' }}>
                <div>LCP</div>
                <div style={{ fontSize: '0.75rem', fontWeight: '400', color: designSystem.colors.textMuted, marginTop: '0.25rem' }}>
                  (최대 콘텐츠 표시)
                </div>
              </th>
              <th style={{ ...tableStyles.th, textAlign: 'center' }}>
                <div>TBT</div>
                <div style={{ fontSize: '0.75rem', fontWeight: '400', color: designSystem.colors.textMuted, marginTop: '0.25rem' }}>
                  (총 차단 시간)
                </div>
              </th>
              <th style={{ ...tableStyles.th, textAlign: 'center' }}>
                <div>페이지 크기</div>
                <div style={{ fontSize: '0.75rem', fontWeight: '400', color: designSystem.colors.textMuted, marginTop: '0.25rem' }}>
                  (전체 리소스)
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            {validResults.map((result) => {
              const isActive = isActiveRow(result.url);
              const isHovered = hoveredUrl === result.url;
              const overallScore = result.customRatings?.overall;
              const desktopScore = result.customRatings?.desktop;
              const mobileScore = result.customRatings?.mobile;
              const fcp = result.pageSpeedData?.mobile?.firstContentfulPaint;
              const lcp = result.pageSpeedData?.mobile?.largestContentfulPaint;
              const tbt = result.pageSpeedData?.mobile?.totalBlockingTime;
              const size = result.pageSpeedData?.mobile?.pageSize?.totalSize;

              return (
                <tr
                  key={result.url}
                  style={{
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    ...(isActive ? tableStyles.activeRow : {}),
                    ...(isHovered && !isActive ? { backgroundColor: `${designSystem.colors.primary}15` } : {})
                  }}
                  onClick={() => onUrlClick(result.url, result.originalIndex)}
                  onMouseEnter={() => setHoveredUrl(result.url)}
                  onMouseLeave={() => setHoveredUrl(null)}
                >
                  {/* 사이트명 */}
                  <td
                    style={{
                      ...tableStyles.td,
                      fontWeight: '600',
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {getUrlLabel(result.url)}
                    {isActive && <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: designSystem.colors.primary }}>(현재)</span>}
                  </td>


                  {/* 전체 점수 */}
                  <td style={{ ...tableStyles.td, textAlign: 'center' }}>
                    {overallScore !== undefined ? (
                      <span style={{
                        ...tableStyles.scoreBadge,
                        backgroundColor: `${getRatingColor(overallScore)}20`,
                        color: getRatingColor(overallScore),
                        border: `2px solid ${getRatingColor(overallScore)}`
                      }}>
                        {Math.round(overallScore)} ({getRatingGrade(overallScore)})
                      </span>
                    ) : '—'}
                  </td>

                  {/* PC / 모바일 점수 */}
                  <td style={{ ...tableStyles.td, textAlign: 'center' }}>
                    {desktopScore !== undefined && mobileScore !== undefined ? (
                      <span>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.3rem 0.5rem',
                          borderRadius: '0.4rem',
                          backgroundColor: `${getRatingColor(desktopScore)}20`,
                          color: getRatingColor(desktopScore),
                          border: `2px solid ${getRatingColor(desktopScore)}`,
                          fontWeight: '600',
                          fontSize: '0.85rem',
                          marginRight: '0.5rem'
                        }}>
                          {Math.round(desktopScore)}
                        </span>
                        /
                        <span style={{
                          display: 'inline-block',
                          padding: '0.3rem 0.5rem',
                          borderRadius: '0.4rem',
                          backgroundColor: `${getRatingColor(mobileScore)}20`,
                          color: getRatingColor(mobileScore),
                          border: `2px solid ${getRatingColor(mobileScore)}`,
                          fontWeight: '600',
                          fontSize: '0.85rem',
                          marginLeft: '0.5rem'
                        }}>
                          {Math.round(mobileScore)}
                        </span>
                      </span>
                    ) : '—'}
                  </td>

                  {/* FCP */}
                  <td style={{ ...tableStyles.td, textAlign: 'center' }}>
                    {fcp !== undefined && fcp !== null ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.4rem 0.75rem',
                        borderRadius: '0.5rem',
                        backgroundColor: `${getMetricColor('FCP', fcp)}25`,
                        color: getMetricColor('FCP', fcp),
                        border: `2px solid ${getMetricColor('FCP', fcp)}`,
                        fontWeight: '600',
                        fontSize: '0.875rem'
                      }}>
                        {getMetricIcon('FCP', fcp)} {formatTime(fcp)}
                      </span>
                    ) : '—'}
                  </td>

                  {/* LCP */}
                  <td style={{ ...tableStyles.td, textAlign: 'center' }}>
                    {lcp !== undefined && lcp !== null ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.4rem 0.75rem',
                        borderRadius: '0.5rem',
                        backgroundColor: `${getMetricColor('LCP', lcp)}25`,
                        color: getMetricColor('LCP', lcp),
                        border: `2px solid ${getMetricColor('LCP', lcp)}`,
                        fontWeight: '600',
                        fontSize: '0.875rem'
                      }}>
                        {getMetricIcon('LCP', lcp)} {formatTime(lcp)}
                      </span>
                    ) : '—'}
                  </td>

                  {/* TBT */}
                  <td style={{ ...tableStyles.td, textAlign: 'center' }}>
                    {tbt !== undefined && tbt !== null ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.4rem 0.75rem',
                        borderRadius: '0.5rem',
                        backgroundColor: `${getMetricColor('TBT', tbt)}25`,
                        color: getMetricColor('TBT', tbt),
                        border: `2px solid ${getMetricColor('TBT', tbt)}`,
                        fontWeight: '600',
                        fontSize: '0.875rem'
                      }}>
                        {getMetricIcon('TBT', tbt)} {formatTBT(tbt)}
                      </span>
                    ) : '—'}
                  </td>

                  {/* 페이지 크기 */}
                  <td style={{ ...tableStyles.td, textAlign: 'center' }}>
                    {size !== undefined && size !== null ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.4rem 0.75rem',
                        borderRadius: '0.5rem',
                        backgroundColor: `${getPageSizeColor(size)}25`,
                        color: getPageSizeColor(size),
                        border: `2px solid ${getPageSizeColor(size)}`,
                        fontWeight: '600',
                        fontSize: '0.875rem'
                      }}>
                        {getPageSizeIcon(size)} {formatSize(size)}
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={tableStyles.hint}>
        행을 클릭하여 해당 보고서로 이동할 수 있습니다
      </div>
    </div>
  );
};

export default MultiUrlComparisonTable;
